import { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useToast } from '@/components/ui/use-toast';
import { useTenant } from '@/contexts/TenantContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SlidePanel } from "@/components/ui/slide-panel";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";


import { supabase } from '@/lib/supabaseClient';
import { getCurrentTenantId } from '@/utils/tenantUtils';
import { Plus, Pencil, Trash2, Star, StarOff, LayoutTemplate, Shield, Sun, Sunrise, SunMedium, Zap, Sparkles, Circle, FileText, Globe, Users, ChevronDown, Download, Upload, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { PipelineAssignments } from './PipelineAssignments';
import { Textarea } from '@/components/ui/textarea';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { Switch } from '@/components/ui/switch';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { DragDropContext, Droppable, Draggable, DropResult, DroppableProvided, DraggableProvided } from '@hello-pangea/dnd';
import { GripVertical } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import { CardLayoutEditor, CardLayoutEditorPreview, CardFieldConfig } from '@/components/settings/CardLayoutEditor';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { PipelineAccessSelect, RoleOption, UserOption, TeamOption } from '@/components/ui/pipeline-access-select';
import { ChecklistAccessSelect } from '@/components/ui/checklist-access-select';
import { ClipboardList } from 'lucide-react';
import { ChecklistsTab } from '@/components/settings/ChecklistsTab';
import { SlaSettings } from './SlaSettings';
import { PipelineStagesManager } from './PipelineStagesManager';

// Type for user data from Supabase
type UserData = {
  id: string;
  first_name: string;
  last_name: string;
};
import { SlaThreshold } from '@/types/pipeline';

interface Pipeline {
  id: number;
  name: string;
  description: string;
  pipeline_type: 'commercial' | 'residential' | 'onboarding';
  created_at: string;
  project_count?: number;
  role_permissions?: string[];
  user_permissions?: string[];
  team_permissions?: string[];
  template_id?: string;
  template_version?: number;
  last_template_sync?: string;
  template?: {
    id: string;
    name: string;
    template_version: number;
  };
}

interface PipelineFormData {
  name: string;
  description: string;
  pipeline_type: 'commercial' | 'residential' | 'onboarding';
}

const DEFAULT_CARD_FIELDS = [
  {
    "icon": "faUsers|fa",
    "field": "team_name",
    "label": "Team Name"
  },
  {
    "icon": "user",
    "field": "sales_rep",
    "label": "Sales Rep"
  },
  {
    "icon": "zap",
    "field": "system_size",
    "label": "System Size"
  },
  {
    "icon": "dollar-sign",
    "field": "project_value",
    "label": "Project Value"
  },
  {
    "icon": "faClipboardList|fa",
    "field": "project_manager",
    "label": "Project Manager"
  },
  {
    "icon": "clock",
    "field": "days_in_stage",
    "label": "Days in Stage"
  }
];

export function PipelinesTab() {
  const { toast } = useToast();
  const { currentTenant } = useTenant();
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPipeline, setEditingPipeline] = useState<Pipeline | null>(null);
  const [formData, setFormData] = useState<PipelineFormData>({
    name: '',
    description: '',
    pipeline_type: 'commercial',
  });
  const [activeType, setActiveType] = useState<'commercial' | 'residential' | 'onboarding'>('commercial');
  const [newAssignments, setNewAssignments] = useState<any[]>([]);
  const [newSelectedUser, setNewSelectedUser] = useState('');
  const [newAssignmentWeight, setNewAssignmentWeight] = useState(50);
  const [newIsEvenSplit, setNewIsEvenSplit] = useState(true);
  const [newUsers, setNewUsers] = useState<any[]>([]);
  // Stage state is now handled by individual PipelineStagesManager components
  
  const [isCardLayoutEditorOpen, setIsCardLayoutEditorOpen] = useState(false);
  const [editingCardLayoutPipeline, setEditingCardLayoutPipeline] = useState<any | null>(null);
  const [cardFields, setCardFields] = useState<CardFieldConfig[]>(DEFAULT_CARD_FIELDS);
  const [cardFieldsLoading, setCardFieldsLoading] = useState(false);
  const [cardFieldsSaving, setCardFieldsSaving] = useState(false);
  const [previewProject, setPreviewProject] = useState<any | null>(null);
  const [stageProjectCounts, setStageProjectCounts] = useState<Record<number, number>>({});
  // Stage operations are now handled by individual PipelineStagesManager components
  
  // Checklist management states
  const [isChecklistDialogOpen, setIsChecklistDialogOpen] = useState(false);
  /**
   * ⚠️ The item form lives three levels down (ChecklistsTab ->
   * ChecklistItemManager, rendered with `noPanel`), so the X and the
   * Close button on THIS panel are the real way people leave a
   * half-finished item. The child registers a guard here and we ask it
   * before closing, rather than closing and losing the work.
   */
  const checklistExitGuard = useRef<((exit: () => void) => void) | null>(null);
  const closeChecklistDialog = () => {
    const close = () => setIsChecklistDialogOpen(false);
    if (checklistExitGuard.current) checklistExitGuard.current(close);
    else close();
  };
  const [selectedPipelineForChecklists, setSelectedPipelineForChecklists] = useState<Pipeline | null>(null);

  // Pipeline access management states
  const [isAccessDialogOpen, setIsAccessDialogOpen] = useState(false);
  const [selectedPipelineForAccess, setSelectedPipelineForAccess] = useState<Pipeline | null>(null);

  // Delete confirmation dialog states
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [pipelineToDelete, setPipelineToDelete] = useState<Pipeline | null>(null);

  // Save as template dialog states
  const [isSaveTemplateDialogOpen, setIsSaveTemplateDialogOpen] = useState(false);
  const [pipelineToSaveAsTemplate, setPipelineToSaveAsTemplate] = useState<Pipeline | null>(null);
  const [availableTemplates, setAvailableTemplates] = useState<any[]>([]);
  const [templateSaveMode, setTemplateSaveMode] = useState<'new' | 'overwrite'>('new');
  const [selectedTemplateToOverwrite, setSelectedTemplateToOverwrite] = useState<string | null>(null);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [showOverwriteConfirmation, setShowOverwriteConfirmation] = useState(false);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [teamOptions, setTeamOptions] = useState<TeamOption[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessSaving, setAccessSaving] = useState(false);

  // Template selection states
  const [pipelineTemplates, setPipelineTemplates] = useState<any[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [creationMode, setCreationMode] = useState<'new' | 'template'>('new');

  // Load/Sync template states
  const [isLoadTemplateDialogOpen, setIsLoadTemplateDialogOpen] = useState(false);
  const [isSyncConfirmationOpen, setIsSyncConfirmationOpen] = useState(false);
  const [pipelineToLoadTemplate, setPipelineToLoadTemplate] = useState<Pipeline | null>(null);
  const [pipelineToSync, setPipelineToSync] = useState<Pipeline | null>(null);
  const [selectedTemplateToLoad, setSelectedTemplateToLoad] = useState<string | null>(null);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [syncDirection, setSyncDirection] = useState<'template-to-pipeline' | 'pipeline-to-template' | 'merge'>('template-to-pipeline');
  const [pipelineStages, setPipelineStages] = useState<any[]>([]);
  const [templateStages, setTemplateStages] = useState<any[]>([]);
  const [pipelineChecklistsData, setPipelineChecklistsData] = useState<any[]>([]);
  const [templateChecklistsData, setTemplateChecklistsData] = useState<any[]>([]);
  const [isLoadingComparison, setIsLoadingComparison] = useState(false);
  const [loadChecklistTemplates, setLoadChecklistTemplates] = useState(true);
  const [availableChecklistTemplates, setAvailableChecklistTemplates] = useState<any[]>([]);
  const [showChecklistDetails, setShowChecklistDetails] = useState(false);
  const [isCreatingPipeline, setIsCreatingPipeline] = useState(false);

  // New pipeline permissions states
  const [newPipelinePermissionsEnabled, setNewPipelinePermissionsEnabled] = useState(false);
  const [newPipelineSelectedRoles, setNewPipelineSelectedRoles] = useState<string[]>([]);
  const [newPipelineSelectedUsers, setNewPipelineSelectedUsers] = useState<string[]>([]);
  const [newPipelineSelectedTeams, setNewPipelineSelectedTeams] = useState<string[]>([]);
  const [newPipelineAccessLoading, setNewPipelineAccessLoading] = useState(false);

  // Helper function to check if a pipeline has template updates available
  const hasTemplateUpdate = (pipeline: Pipeline) => {
    return pipeline.template_id &&
      pipeline.template?.template_version &&
      pipeline.template_version &&
      pipeline.template.template_version > pipeline.template_version;
  };

  // Helper component for template update badge (removed - now using icon badge)

  useEffect(() => {
    fetchPipelines();
    fetchPipelineTemplates();
  }, [currentTenant?.tenant_id]);

  useEffect(() => {
    if (isDialogOpen) {
      supabase
        .from('users')
        .select('id, first_name, last_name, email')
        .order('first_name', { ascending: true })
        .then(({ data }) => setNewUsers(data || []));
      
      // Load permission options for new pipeline if not editing
      if (!editingPipeline) {
        loadNewPipelinePermissionOptions();
      }
    }
  }, [isDialogOpen]);

  const loadNewPipelinePermissionOptions = async () => {
    setNewPipelineAccessLoading(true);
    try {
      // Get current tenant ID
      const { data: tenantId, error: tenantError } = await supabase.rpc('get_current_user_tenant_id');
      if (tenantError) {
        console.error('Error getting tenant ID:', tenantError);
        throw new Error('Failed to get tenant ID');
      }

      const [rolesResponse, usersResponse, teamsResponse] = await Promise.all([
        supabase
          .from('roles')
          .select('id, name, description')
          .eq('tenant_id', tenantId)
          .order('name'),
        supabase
          .from('users')
          .select('id, first_name, last_name, email, roles(name)')
          .eq('tenant_id', tenantId)
          .order('first_name', { ascending: true }),
        supabase
          .from('teams')
          .select(`
            id,
            name,
            type,
            manager_id,
            biz_dev_id
          `)
          .eq('tenant_id', tenantId)
          .order('name')
      ]);

      if (rolesResponse.error) throw rolesResponse.error;
      if (usersResponse.error) throw usersResponse.error;
      if (teamsResponse.error) throw teamsResponse.error;

      // Format role options
      const formattedRoles: RoleOption[] = (rolesResponse.data || []).map(role => ({
        label: role.name,
        value: role.name,
        description: `Allow users with ${role.name} role to access this pipeline`
      }));

      // Format user options
      const formattedUsers: UserOption[] = (usersResponse.data || []).map(user => {
        const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
        const displayName = fullName || user.email;
        
        let userRole = '';
        if (user.roles) {
          if (Array.isArray(user.roles)) {
            userRole = user.roles[0]?.name || '';
          } else if (typeof user.roles === 'object') {
            userRole = (user.roles as any).name || '';
          }
        }

        return {
          label: displayName,
          value: user.id,
          email: user.email,
          role: userRole
        };
      });

      const teamData = teamsResponse.data || [];
      const uniqueManagerIds = Array.from(new Set(teamData.map(team => team.manager_id).filter(Boolean)));
      const uniqueBizDevIds = Array.from(new Set(teamData.map(team => team.biz_dev_id).filter(Boolean)));
      const allManagerIds = [...uniqueManagerIds, ...uniqueBizDevIds];

      interface ManagerDetails {
        id: string;
        first_name: string | null;
        last_name: string | null;
      }

      let managerDetails: ManagerDetails[] = [];
      if (allManagerIds.length > 0) {
        const { data: managers } = await supabase
          .from('users')
          .select('id, first_name, last_name')
          .in('id', allManagerIds);
        managerDetails = (managers as ManagerDetails[]) || [];
      }

      const formattedTeams: TeamOption[] = teamData.map(team => {
        const manager = managerDetails.find(m => m.id === team.manager_id);
        const bizDev = managerDetails.find(m => m.id === team.biz_dev_id);
        
        let subtitle = '';
        if (manager && bizDev && manager.id !== bizDev.id) {
          subtitle = `Manager: ${manager.first_name} ${manager.last_name}, Biz Dev: ${bizDev.first_name} ${bizDev.last_name}`;
        } else if (manager) {
          subtitle = `Manager: ${manager.first_name} ${manager.last_name}`;
        } else if (bizDev) {
          subtitle = `Biz Dev: ${bizDev.first_name} ${bizDev.last_name}`;
        }

        return {
          label: team.name,
          value: team.id.toString(),
          subtitle: subtitle,
          type: team.type
        };
      });

      // Use the same state variables that exist for access dialog
      setRoleOptions(formattedRoles);
      setUserOptions(formattedUsers);
      setTeamOptions(formattedTeams);

    } catch (error) {
      console.error('Error loading permission options:', error);
    } finally {
      setNewPipelineAccessLoading(false);
    }
  };

  const fetchPipelines = async () => {
    try {
      // Get current tenant ID to filter pipelines
      const tenantId = await getCurrentTenantId();
      if (!tenantId) {
        throw new Error('No tenant ID found');
      }

      const { data, error} = await supabase
        .from('pipelines')
        .select(`
          *,
          template:pipeline_templates_library!template_id(
            id,
            name,
            template_version
          )
        `)
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch project counts separately with proper filtering for deleted projects
      const pipelinesWithCount = await Promise.all(
        data.map(async (pipeline: any) => {
          const { count, error: countError } = await supabase
            .from('projects')
            .select('*', { count: 'exact', head: true })
            .eq('pipeline_id', pipeline.id)
            .is('deleted_at', null);  // Only count non-deleted projects

          if (countError) {
            console.error('Error counting projects for pipeline:', pipeline.id, countError);
          }

          return {
            ...pipeline,
            project_count: count || 0,
          };
        })
      );

      setPipelines(pipelinesWithCount);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPipelineTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('pipeline_templates_library')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) {
        console.error('Error fetching pipeline templates:', error);
        return;
      }

      setPipelineTemplates(data || []);
    } catch (error) {
      console.error('Error fetching pipeline templates:', error);
    }
  };

  const handleCreateFromScratch = () => {
    setCreationMode('new');
    setSelectedTemplate(null);
    setFormData({
      name: '',
      description: '',
      pipeline_type: activeType,
    });
  };

  const handleSelectTemplate = async (template: any) => {
    setCreationMode('template');
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      description: template.description,
      pipeline_type: template.pipeline_type,
    });
    
    // Fetch available checklist templates for this pipeline template
    await fetchAvailableChecklistTemplates(template);
  };

  const fetchAvailableChecklistTemplates = async (template: any) => {
    if (!template?.stages) {
      setAvailableChecklistTemplates([]);
      return;
    }

    try {
      const stageNames = template.stages.map((stage: any) => stage.name);
      
      const { data, error } = await supabase
        .from('checklist_templates_library')
        .select('*')
        .or(`pipeline_id.is.null,pipeline_name.ilike.%${template.pipeline_type}%`)
        .in('stage_name', stageNames);

      if (error) {
        console.error('Error fetching checklist templates:', error);
        setAvailableChecklistTemplates([]);
        return;
      }

      setAvailableChecklistTemplates(data || []);
    } catch (error) {
      console.error('Error fetching checklist templates:', error);
      setAvailableChecklistTemplates([]);
    }
  };

  const handleLoadTemplateStages = async (pipelineId: number) => {
    if (!selectedTemplate?.stages) return;

    try {
      const tenantId = await getCurrentTenantId();
      
      // Create stages from template
      const stagesToCreate = selectedTemplate.stages.map((stage: any, index: number) => ({
        pipeline_id: pipelineId,
        name: stage.name,
        description: stage.description,
        stage_order: index + 1,
        sla_enabled: stage.sla_enabled || false,
        sla_settings: stage.sla_settings || null,
        permissions: stage.permissions || null,
        user_permissions: stage.user_permissions || [],
        tenant_id: tenantId
      }));

      const { error } = await supabase
        .from('stages')
        .insert(stagesToCreate);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Pipeline created with ${stagesToCreate.length} stages from template`,
      });
    } catch (error: any) {
      console.error('Error creating stages from template:', error);
      toast({
        title: "Error",
        description: "Failed to create stages from template",
        variant: "destructive",
      });
    }
  };

  const handleLoadChecklistTemplates = async (pipelineId: number) => {
    if (!availableChecklistTemplates.length || !selectedTemplate?.stages) return;

    try {
      const tenantId = await getCurrentTenantId();
      
      // Get the newly created stages to map template checklists to actual stage IDs
      const { data: stages, error: stagesError } = await supabase
        .from('stages')
        .select('id, name, stage_order')
        .eq('pipeline_id', pipelineId)
        .order('stage_order');

      if (stagesError) throw stagesError;

      const stageMap = new Map(stages.map(stage => [stage.name, stage.id]));
      const checklistsToCreate = [];

      // Create checklist templates for matching stages
      for (const checklistTemplate of availableChecklistTemplates) {
        if (checklistTemplate.stage_name && stageMap.has(checklistTemplate.stage_name)) {
          const stageId = stageMap.get(checklistTemplate.stage_name);
          
          checklistsToCreate.push({
            name: checklistTemplate.name,
            description: checklistTemplate.description || '',
            stage_id: stageId,
            pipeline_id: pipelineId,
            tenant_id: tenantId
          });
        }
      }

      if (checklistsToCreate.length > 0) {
        const { error: checklistError } = await supabase
          .from('checklist_templates')
          .insert(checklistsToCreate);

        if (checklistError) throw checklistError;

        // Also create checklist template items and groups for each checklist
        for (let i = 0; i < checklistsToCreate.length; i++) {
          const originalTemplate = availableChecklistTemplates[i];
          const templateConfig = originalTemplate.configuration;
          
          const { data: createdTemplate } = await supabase
            .from('checklist_templates')
            .select('id')
            .eq('name', checklistsToCreate[i].name)
            .eq('stage_id', checklistsToCreate[i].stage_id)
            .single();

          if (createdTemplate) {
            // Step 1: Copy groups from the original library template if they exist
            const groupIdMapping = new Map();
            
            // Try to find the source template by name to copy groups from
            // For "Site Survey - Residential", we want to copy from "Site Survey" (template 47)
            let sourceTemplateId = null;
            if (originalTemplate.name === 'Site Survey - Residential') {
              sourceTemplateId = 47; // Template "Site Survey" has the groups
            }
            
            if (sourceTemplateId) {
              // Fetch groups from the source template
              let originalGroups, groupsError;
              
              // Try direct SQL query first to bypass any client-side session issues
              try {
                const { data: rpcGroups, error: rpcError } = await supabase
                  .rpc('get_checklist_groups_for_template', { template_id_param: sourceTemplateId });
                
                if (!rpcError) {
                  originalGroups = rpcGroups;
                  groupsError = null;
                } else {
                  throw rpcError;
                }
              } catch (rpcError) {
                // Fallback to original query
                const result = await supabase
                  .from('checklist_groups')
                  .select('*')
                  .eq('template_id', sourceTemplateId)
                  .order('group_order');
                
                originalGroups = result.data;
                groupsError = result.error;
              }

              if (groupsError) {
                console.error('Error fetching original groups:', groupsError);
                // Continue without groups rather than failing completely
              }

              if (originalGroups && originalGroups.length > 0) {
                // Copy groups to the new template
                const groupsToCreate = originalGroups.map((group: any) => ({
                  template_id: createdTemplate.id,
                  group_name: group.group_name,
                  group_order: group.group_order,
                  permissions: group.permissions,
                  conditional_logic: group.conditional_logic,
                  user_permissions: group.user_permissions,
                  tenant_id: tenantId
                }));

                const { data: createdGroups, error: groupInsertError } = await supabase
                  .from('checklist_groups')
                  .insert(groupsToCreate)
                  .select('id, group_name');

                if (groupInsertError) {
                  console.error('Error creating groups:', groupInsertError);
                  // Continue without groups rather than failing completely
                }

                if (createdGroups) {
                  // Create mapping from original group IDs to new group IDs
                  
                  originalGroups.forEach((originalGroup: any, index: number) => {
                    groupIdMapping.set(originalGroup.id, createdGroups[index].id);
                  });
                }
              }
            }

            // Step 2: Copy checklist items with proper group mappings
            if (templateConfig?.items?.length > 0) {
              
              // First check if we should get items from source template instead
              if (sourceTemplateId) {
                
                const { data: originalItems, error: itemsError } = await supabase
                  .from('checklist_items')
                  .select('*')
                  .eq('template_id', sourceTemplateId)
                  .is('deleted_at', null)
                  .order('item_order');

                if (originalItems && originalItems.length > 0) {
                  
                  const itemsToCreate = originalItems.map(item => {
                    const newGroupId = item.group_id ? groupIdMapping.get(item.group_id) || null : null;
                    
                    return {
                      template_id: createdTemplate.id,
                      item_name: item.item_name,
                      item_type: item.item_type,
                      item_order: item.item_order,
                      is_required: item.is_required,
                      item_values: item.item_values,
                      group_id: newGroupId,
                      conditional_logic: item.conditional_logic, // We'll update field references after insert
                      tenant_id: tenantId
                    };
                  });

                  // Insert items first to get their new IDs
                  const { data: createdItems, error: itemInsertError } = await supabase
                    .from('checklist_items')
                    .insert(itemsToCreate)
                    .select('id, item_name, item_order');

                  if (itemInsertError) {
                    console.error('Error creating items (Step 2):', itemInsertError);
                  } else if (createdItems) {
                    // Create mapping from original item IDs to new item IDs
                    const itemIdMapping = new Map();
                    originalItems.forEach((originalItem, index) => {
                      if (createdItems[index]) {
                        itemIdMapping.set(originalItem.id, createdItems[index].id);
                      }
                    });

                    // Update conditional logic field references for items
                    const itemUpdates: Array<{ id: number; conditional_logic: any }> = [];
                    originalItems.forEach((originalItem: any, index: number) => {
                      if (originalItem.conditional_logic && createdItems[index]) {
                        const updatedLogic = updateConditionalLogicFieldReferences(originalItem.conditional_logic, itemIdMapping);
                        if (JSON.stringify(updatedLogic) !== JSON.stringify(originalItem.conditional_logic)) {
                          itemUpdates.push({
                            id: createdItems[index].id,
                            conditional_logic: updatedLogic
                          });
                        }
                      }
                    });

                    // Batch update items with corrected conditional logic
                    if (itemUpdates.length > 0) {
                      for (const update of itemUpdates) {
                        await supabase
                          .from('checklist_items')
                          .update({ conditional_logic: update.conditional_logic })
                          .eq('id', update.id);
                      }
                    }

                    // Update conditional logic field references for groups (need to refetch them for this template)
                    if (sourceTemplateId) {
                      // Get the original groups for field reference mapping
                      const { data: sourceGroups } = await supabase
                        .rpc('get_checklist_groups_for_template', { template_id_param: sourceTemplateId })
                        .order('group_order');

                      // Get the newly created groups for this template
                      const { data: newlyCreatedGroups } = await supabase
                        .from('checklist_groups')
                        .select('id, group_name, group_order')
                        .eq('template_id', createdTemplate.id)
                        .order('group_order');

                      if (sourceGroups && sourceGroups.length > 0 && newlyCreatedGroups && newlyCreatedGroups.length > 0) {
                        const groupUpdates: Array<{ id: string; conditional_logic: any }> = [];
                        sourceGroups.forEach((originalGroup: any, index: number) => {
                          if (originalGroup.conditional_logic && newlyCreatedGroups[index]) {
                            const updatedLogic = updateConditionalLogicFieldReferences(originalGroup.conditional_logic, itemIdMapping);
                            if (JSON.stringify(updatedLogic) !== JSON.stringify(originalGroup.conditional_logic)) {
                              groupUpdates.push({
                                id: newlyCreatedGroups[index].id,
                                conditional_logic: updatedLogic
                              });
                            }
                          }
                        });

                        // Batch update groups with corrected conditional logic
                        if (groupUpdates.length > 0) {
                          for (const update of groupUpdates) {
                            await supabase
                              .from('checklist_groups')
                              .update({ conditional_logic: update.conditional_logic })
                              .eq('id', update.id);
                          }
                        }
                      }
                    }
                  }
                } else {
                  // Fallback to original logic
                  const itemsToCreate = templateConfig.items.map((item: any, index: number) => ({
                    template_id: createdTemplate.id,
                    item_name: item.name,
                    item_type: item.item_type || 'CHECKBOX',
                    item_order: index + 1,
                    is_required: item.is_required || false,
                    group_id: item.group_id ? groupIdMapping.get(item.group_id) || null : null,
                    tenant_id: tenantId
                  }));

                  await supabase
                    .from('checklist_items')
                    .insert(itemsToCreate);
                }
              } else {
                // Original logic for when there's no source template
                const itemsToCreate = templateConfig.items.map((item: any, index: number) => ({
                  template_id: createdTemplate.id,
                  item_name: item.name,
                  item_type: item.item_type || 'CHECKBOX',
                  item_order: index + 1,
                  is_required: item.is_required || false,
                  group_id: item.group_id ? groupIdMapping.get(item.group_id) || null : null,
                  tenant_id: tenantId
                }));

                await supabase
                  .from('checklist_items')
                  .insert(itemsToCreate);
              }
            }

            // Step 3: Also copy any items directly from the source template (for templates without configuration)
            
            if (sourceTemplateId && (!templateConfig?.items || templateConfig.items.length === 0)) {
              const { data: originalItems, error: itemsError } = await supabase
                .from('checklist_items')
                .select('*')
                .eq('template_id', sourceTemplateId)
                .is('deleted_at', null)
                .order('item_order');

              if (originalItems && originalItems.length > 0) {
                
                const itemsToCreate = originalItems.map(item => {
                  const newGroupId = item.group_id ? groupIdMapping.get(item.group_id) || null : null;
                  
                  return {
                    template_id: createdTemplate.id,
                    item_name: item.item_name,
                    item_type: item.item_type,
                    item_order: item.item_order,
                    is_required: item.is_required,
                    item_values: item.item_values,
                    group_id: newGroupId,
                    conditional_logic: item.conditional_logic, // We'll update field references after insert
                    tenant_id: tenantId
                  };
                });

                // Insert items first to get their new IDs
                const { data: createdItems, error: itemInsertError } = await supabase
                  .from('checklist_items')
                  .insert(itemsToCreate)
                  .select('id, item_name, item_order');

                if (itemInsertError) {
                  console.error('Error creating items:', itemInsertError);
                } else if (createdItems) {
                  // Create mapping from original item IDs to new item IDs
                  const itemIdMapping = new Map();
                  originalItems.forEach((originalItem, index) => {
                    if (createdItems[index]) {
                      itemIdMapping.set(originalItem.id, createdItems[index].id);
                    }
                  });

                  // Update conditional logic field references for items
                  const itemUpdates: Array<{ id: number; conditional_logic: any }> = [];
                  originalItems.forEach((originalItem: any, index: number) => {
                    if (originalItem.conditional_logic && createdItems[index]) {
                      const updatedLogic = updateConditionalLogicFieldReferences(originalItem.conditional_logic, itemIdMapping);
                      if (JSON.stringify(updatedLogic) !== JSON.stringify(originalItem.conditional_logic)) {
                        itemUpdates.push({
                          id: createdItems[index].id,
                          conditional_logic: updatedLogic
                        });
                      }
                    }
                  });

                  // Batch update items with corrected conditional logic
                  if (itemUpdates.length > 0) {
                    for (const update of itemUpdates) {
                      await supabase
                        .from('checklist_items')
                        .update({ conditional_logic: update.conditional_logic })
                        .eq('id', update.id);
                    }
                  }

                  // Update conditional logic field references for groups (same approach as Step 2)
                  if (sourceTemplateId) {
                    // Get the original groups for field reference mapping
                    const { data: sourceGroups } = await supabase
                      .rpc('get_checklist_groups_for_template', { template_id_param: sourceTemplateId })
                      .order('group_order');

                    // Get the newly created groups for this template
                    const { data: newlyCreatedGroups } = await supabase
                      .from('checklist_groups')
                      .select('id, group_name, group_order')
                      .eq('template_id', createdTemplate.id)
                      .order('group_order');

                    if (sourceGroups && sourceGroups.length > 0 && newlyCreatedGroups && newlyCreatedGroups.length > 0) {
                      const groupUpdates: Array<{ id: string; conditional_logic: any }> = [];
                      sourceGroups.forEach((originalGroup: any, index: number) => {
                        if (originalGroup.conditional_logic && newlyCreatedGroups[index]) {
                          const updatedLogic = updateConditionalLogicFieldReferences(originalGroup.conditional_logic, itemIdMapping);
                          if (JSON.stringify(updatedLogic) !== JSON.stringify(originalGroup.conditional_logic)) {
                            groupUpdates.push({
                              id: newlyCreatedGroups[index].id,
                              conditional_logic: updatedLogic
                            });
                          }
                        }
                      });

                      // Batch update groups with corrected conditional logic
                      if (groupUpdates.length > 0) {

                        for (const update of groupUpdates) {
                          await supabase
                            .from('checklist_groups')
                            .update({ conditional_logic: update.conditional_logic })
                            .eq('id', update.id);
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }

        toast({
          title: "Success",
          description: `Created ${checklistsToCreate.length} checklist templates from template library`,
        });
      }
    } catch (error: any) {
      console.error('Error creating checklist templates:', error);
      toast({
        title: "Warning",
        description: "Pipeline created but failed to load some checklist templates",
        variant: "destructive",
      });
    }
  };

  // Helper function to update field references in conditional logic
  const updateConditionalLogicFieldReferences = (conditionalLogic: any, itemIdMapping: Map<number, number>) => {
    if (!conditionalLogic || !Array.isArray(conditionalLogic)) return conditionalLogic;
    
    return conditionalLogic.map((group: any) => ({
      ...group,
      rules: group.rules?.map((rule: any) => {
        if (rule.field && itemIdMapping.has(parseInt(rule.field))) {
          const newFieldId = itemIdMapping.get(parseInt(rule.field));
          return {
            ...rule,
            field: newFieldId?.toString() || rule.field
          };
        }
        return rule;
      }) || []
    }));
  };

  const handleOpenDialog = (pipeline?: Pipeline) => {
    if (pipeline) {
      setEditingPipeline(pipeline);
      setFormData({
        name: pipeline.name,
        description: pipeline.description,
        pipeline_type: pipeline.pipeline_type,
      });
      setIsDialogOpen(true);
    } else {
      // For new pipeline, open dialog directly with creation mode
      setEditingPipeline(null);
      setFormData({
        name: '',
        description: '',
        pipeline_type: activeType,
      });
      // Reset new pipeline permission states
      setNewPipelinePermissionsEnabled(false);
      setNewPipelineSelectedRoles([]);
      setNewPipelineSelectedUsers([]);
      setNewPipelineSelectedTeams([]);
      setSelectedTemplate(null);
      setCreationMode('new');
      setLoadChecklistTemplates(true);
      setAvailableChecklistTemplates([]);
      setShowChecklistDetails(false);
      setIsCreatingPipeline(false);
      setIsDialogOpen(true);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast({
        title: "Error",
        description: "Pipeline name is required",
        variant: "destructive",
      });
      return;
    }

    setIsCreatingPipeline(true);
    try {
      if (editingPipeline) {
        // Update existing pipeline
        const { error } = await supabase
          .from('pipelines')
          .update({
            name: formData.name.trim(),
            description: formData.description.trim(),
            pipeline_type: formData.pipeline_type,
          })
          .eq('id', editingPipeline.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Pipeline updated successfully",
        });
      } else {
        // Create new pipeline
        const pipelineData: any = {
          name: formData.name.trim(),
          description: formData.description.trim(),
          pipeline_type: formData.pipeline_type
        };

        // Add permissions if enabled
        if (newPipelinePermissionsEnabled) {
          if (newPipelineSelectedRoles.length > 0) {
            pipelineData.role_permissions = newPipelineSelectedRoles;
          }
          if (newPipelineSelectedUsers.length > 0) {
            pipelineData.user_permissions = newPipelineSelectedUsers;
          }
          if (newPipelineSelectedTeams.length > 0) {
            pipelineData.team_permissions = newPipelineSelectedTeams;
          }
        }

        const tenantId = await getCurrentTenantId();
        
        const { data, error: pipelineError } = await supabase
          .from('pipelines')
          .insert([{
            ...pipelineData,
            tenant_id: tenantId
          }])
          .select('id');
        if (pipelineError) throw pipelineError;
        const newPipelineId = Array.isArray(data) && data.length > 0 ? data[0].id : undefined;
        if (!newPipelineId) throw new Error('Failed to get new pipeline ID');

        // Create default pipeline card settings
        try {
          const { error: cardError } = await supabase
            .from('pipeline_card_info')
            .insert({
              pipeline_id: newPipelineId,
              display_fields: DEFAULT_CARD_FIELDS,
              tenant_id: tenantId
            });
          
          if (cardError) {
            console.error('Error creating default card settings:', cardError);
            // Don't throw here - card settings are not critical for pipeline creation
          }
        } catch (cardError) {
          console.error('Error creating default card settings:', cardError);
          // Continue with pipeline creation even if card settings fail
        }

        if (newAssignments.length > 0) {
          await Promise.all(newAssignments.map(a =>
            supabase.from('pipeline_assignments').insert({
              user_id: a.user_id,
              pipeline_id: newPipelineId,
              assignment_weight: a.assignment_weight,
              is_active: a.is_active,
              tenant_id: tenantId
            })
          ));
        }

        // If template was selected, create stages from template
        if (selectedTemplate?.stages) {
          await handleLoadTemplateStages(newPipelineId);
          
          // Also load checklist templates if requested
          if (loadChecklistTemplates && availableChecklistTemplates.length > 0) {
            await handleLoadChecklistTemplates(newPipelineId);
          }
        }

        const checklistMessage = loadChecklistTemplates && availableChecklistTemplates.length > 0 
          ? ` and ${availableChecklistTemplates.length} checklist templates`
          : '';
        
        toast({
          title: "Success",
          description: selectedTemplate 
            ? `Pipeline created successfully with ${selectedTemplate.stages.length} stages from template${checklistMessage}`
            : "Pipeline created successfully",
        });
      }

      setIsDialogOpen(false);
      setSelectedTemplate(null);
      fetchPipelines();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsCreatingPipeline(false);
    }
  };

  const handleDeletePipeline = (pipeline: Pipeline) => {
    if (typeof pipeline.project_count === 'number' && pipeline.project_count > 0) {
      toast({
        title: "Error",
        description: "Cannot delete pipeline with existing projects",
        variant: "destructive",
      });
      return;
    }

    // Open confirmation dialog
    setPipelineToDelete(pipeline);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeletePipeline = async () => {
    if (!pipelineToDelete) return;

    try {
      console.log('Deleting pipeline:', pipelineToDelete.id, pipelineToDelete.name);

      // First, clean up any soft-deleted projects using RPC (bypasses RLS)
      console.log('Cleaning up soft-deleted projects for pipeline:', pipelineToDelete.id);
      const { data: cleanedCount, error: cleanupError } = await supabase
        .rpc('cleanup_soft_deleted_projects', {
          p_pipeline_id: pipelineToDelete.id
        });

      if (cleanupError) {
        console.error('Cleanup error:', cleanupError);
        throw new Error(`Failed to clean up deleted projects: ${cleanupError.message}`);
      }

      console.log('Cleaned up soft-deleted projects:', cleanedCount);

      // Now delete the pipeline
      const { error } = await supabase
        .from('pipelines')
        .delete()
        .eq('id', pipelineToDelete.id);

      if (error) throw error;

      setPipelines(pipelines.filter(p => p.id !== pipelineToDelete.id));
      toast({
        title: "Success",
        description: "Pipeline deleted successfully",
      });

      // Close dialog and clear state
      setIsDeleteDialogOpen(false);
      setPipelineToDelete(null);
    } catch (error: any) {
      console.error('Pipeline delete error:', error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleOpenSaveTemplateDialog = async (pipeline: Pipeline) => {
    try {
      setPipelineToSaveAsTemplate(pipeline);
      setNewTemplateName(`${pipeline.name} Template`);
      setTemplateSaveMode('new');
      setSelectedTemplateToOverwrite(null);

      // Fetch available templates to allow overwriting
      // Include both local templates (current tenant) and global templates
      const { data: templates, error } = await supabase
        .from('pipeline_templates_library')
        .select('id, name, pipeline_type, description, scope')
        .eq('pipeline_type', pipeline.pipeline_type)
        .order('name');

      if (error) throw error;
      setAvailableTemplates(templates || []);
      setIsSaveTemplateDialogOpen(true);
    } catch (error: any) {
      console.error('Error loading templates:', error);
      toast({
        title: "Error",
        description: "Failed to load templates",
        variant: "destructive"
      });
    }
  };

  const handleSaveAsTemplateClick = () => {
    // If overwriting, show confirmation dialog first
    if (templateSaveMode === 'overwrite' && selectedTemplateToOverwrite) {
      setShowOverwriteConfirmation(true);
    } else {
      // For new templates, proceed directly
      handleSaveAsTemplate();
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!pipelineToSaveAsTemplate) return;

    // Close confirmation dialog if it was open
    setShowOverwriteConfirmation(false);
    setIsSavingTemplate(true);

    try {
      const pipeline = pipelineToSaveAsTemplate;
      const tenantId = await getCurrentTenantId();

      // Get current user ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Fetch the pipeline's stages in correct order
      const { data: stages, error: stagesError } = await supabase
        .from('stages')
        .select('*')
        .eq('pipeline_id', pipeline.id)
        .order('stage_order', { ascending: true });

      if (stagesError) throw stagesError;

      let pipelineTemplateId: string;

      if (templateSaveMode === 'overwrite' && selectedTemplateToOverwrite) {
        // Overwrite existing template
        // First, delete existing checklist templates linked to this template
        await supabase
          .from('checklist_templates_library')
          .delete()
          .eq('pipeline_template_id', selectedTemplateToOverwrite);

        // Get current template to increment version
        const { data: currentTemplate } = await supabase
          .from('pipeline_templates_library')
          .select('template_version')
          .eq('id', selectedTemplateToOverwrite)
          .single();

        const newVersion = (currentTemplate?.template_version || 0) + 1;

        // Update the existing template
        const { data: templateData, error: templateError } = await supabase
          .from('pipeline_templates_library')
          .update({
            description: `Template updated from ${pipeline.name} pipeline`,
            stages: stages || [],
            updated_at: new Date().toISOString(),
            template_version: newVersion
          })
          .eq('id', selectedTemplateToOverwrite)
          .select()
          .single();

        if (templateError) throw templateError;
        pipelineTemplateId = templateData.id;
      } else {
        // Create new template
        const { data: templateData, error: templateError } = await supabase
          .from('pipeline_templates_library')
          .insert({
            name: newTemplateName,
            description: `Template created from ${pipeline.name} pipeline`,
            pipeline_type: pipeline.pipeline_type,
            scope: 'tenant',
            stages: stages || [],
            is_active: true,
            created_by: user.id,
            owner_tenant_id: tenantId,
            template_version: 1
          })
          .select()
          .single();

        if (templateError) throw templateError;
        pipelineTemplateId = templateData.id;
      }

      // Now copy checklist templates from the pipeline with their stage information
      const { data: checklistTemplates, error: checklistError } = await supabase
        .from('checklist_templates')
        .select(`
          *,
          stages:stage_id(id, name, stage_order)
        `)
        .eq('pipeline_id', pipeline.id)
        .order('display_order', { ascending: true });

      if (checklistError) {
        console.warn('No checklist templates found for pipeline:', checklistError);
      } else if (checklistTemplates && checklistTemplates.length > 0) {
        // Copy each checklist template to the library with proper linking
        const checklistTemplatePromises = checklistTemplates.map(async (checklist) => {
          // Fetch checklist items for this template
          let items: any[] = [];
          let groups: any[] = [];

          try {
            const { data: itemsData, error: itemsError } = await supabase
              .from('checklist_items')
              .select('*')
              .eq('template_id', checklist.id)
              .order('item_order', { ascending: true });

            if (!itemsError && itemsData) {
              // Map items to template format with all fields
              items = itemsData.map((item) => ({
                id: `item-${item.id}`, // Use string ID for templates
                name: item.item_name, // Use item_name field
                item_order: item.item_order,
                item_type: item.item_type || 'CHECKBOX',
                is_required: item.is_required || false,
                item_values: item.item_values || null,
                item_category: item.item_category || 'general',
                description: item.description || null,
                group_id: item.group_id ? `group-${item.group_id}` : null, // Convert to string ID format to match groups
                permissions: item.permissions || null,
                conditional_logic: item.conditional_logic || null,
                user_permissions: item.user_permissions || null
              }));
            }
          } catch (error) {
            console.warn(`Could not fetch items for checklist ${checklist.id}:`, error);
          }

          // Fetch checklist groups
          try {
            const { data: groupsData, error: groupsError } = await supabase
              .from('checklist_groups')
              .select('*')
              .eq('template_id', checklist.id)
              .order('group_order', { ascending: true });

            if (!groupsError && groupsData) {
              // Map groups to template format
              groups = groupsData.map((group) => ({
                id: `group-${group.id}`,
                group_name: group.group_name,
                group_order: group.group_order,
                permissions: group.permissions || null,
                conditional_logic: group.conditional_logic || null,
                user_permissions: group.user_permissions || null
              }));
            }
          } catch (error) {
            console.warn(`Could not fetch groups for checklist ${checklist.id}:`, error);
          }

          // Get stage name from the joined stages data or lookup in stages array
          let stageName = 'Unknown Stage';

          // First try to get from the joined stages data (most reliable)
          if (checklist.stages && typeof checklist.stages === 'object') {
            stageName = checklist.stages.name;
          }
          // Fallback to looking up in the stages array
          else if (checklist.stage_id) {
            const foundStage = stages?.find(s => s.id === checklist.stage_id);
            if (foundStage) {
              stageName = foundStage.name;
            }
          }

          // Create the checklist template in the library
          return supabase
            .from('checklist_templates_library')
            .insert({
              name: checklist.name,
              description: checklist.description,
              category: checklist.checklist_type || 'general',
              stage_name: stageName,
              display_order: checklist.display_order ?? null,
              pipeline_id: pipeline.id, // Keep reference to original pipeline
              pipeline_name: pipeline.name,
              pipeline_template_id: pipelineTemplateId, // Link to the new pipeline template
              scope: 'tenant',
              configuration: {
                items: items || [],
                groups: groups || [],
                display_order: checklist.display_order || 0, // Preserve original display order
                default_settings: {
                  auto_notify_pm_at_percent: 80,
                  auto_move_stage_when_complete: false,
                  estimated_completion_time_hours: 2
                }
              },
              total_items_count: items?.length || 0,
              is_active: true,
              is_legacy: false, // This is a new, properly linked template
              created_by: user.id,
              owner_tenant_id: tenantId
            });
        });

        await Promise.all(checklistTemplatePromises);
      }

      // Link the pipeline back to the template for version tracking
      const { data: updatedTemplate } = await supabase
        .from('pipeline_templates_library')
        .select('template_version')
        .eq('id', pipelineTemplateId)
        .single();

      await supabase
        .from('pipelines')
        .update({
          template_id: pipelineTemplateId,
          template_version: updatedTemplate?.template_version || 1,
          last_template_sync: new Date().toISOString()
        })
        .eq('id', pipeline.id);

      toast({
        title: "Success",
        description: templateSaveMode === 'overwrite'
          ? "Template updated successfully"
          : "Pipeline saved as template successfully"
      });

      // Refresh pipelines to show updated template tracking
      await fetchPipelines();

      // Close dialog and reset state
      setIsSaveTemplateDialogOpen(false);
      setPipelineToSaveAsTemplate(null);
      setTemplateSaveMode('new');
      setSelectedTemplateToOverwrite(null);
      setNewTemplateName('');
    } catch (error: any) {
      console.error('Error saving as template:', error);
      toast({
        title: "Error",
        description: "Failed to save pipeline as template",
        variant: "destructive"
      });
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const handleSetDefaultPipeline = async (pipeline: Pipeline) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('user_pipeline_preferences')
        .upsert({
          user_id: user.id,
          [`default_${pipeline.pipeline_type}_pipeline_id`]: pipeline.id,
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: `Default ${pipeline.pipeline_type} pipeline set successfully`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleTypeChange = (type: 'commercial' | 'residential' | 'onboarding') => {
    setActiveType(type);
  };

  const handleOpenChecklistDialog = (pipeline: Pipeline) => {
    setSelectedPipelineForChecklists(pipeline);
    setIsChecklistDialogOpen(true);
  };

  const handleOpenAccessDialog = async (pipeline: Pipeline) => {
    setSelectedPipelineForAccess(pipeline);
    setAccessLoading(true);
    
    try {
      // Get current tenant ID
      const { data: tenantId, error: tenantError } = await supabase.rpc('get_current_user_tenant_id');
      if (tenantError) {
        console.error('Error getting tenant ID:', tenantError);
        throw new Error('Failed to get tenant ID');
      }

      // Fetch roles, users, and teams
      const [rolesResponse, usersResponse, teamsResponse] = await Promise.all([
        supabase
          .from('roles')
          .select('id, name, description')
          .eq('tenant_id', tenantId)
          .order('name'),
        supabase
          .from('users')
          .select('id, first_name, last_name, email, roles(name)')
          .eq('tenant_id', tenantId)
          .order('first_name', { ascending: true }),
        supabase
          .from('teams')
          .select(`
            id,
            name,
            type,
            manager_id,
            biz_dev_id
          `)
          .eq('tenant_id', tenantId)
          .order('name')
      ]);

      if (rolesResponse.error) throw rolesResponse.error;
      if (usersResponse.error) throw usersResponse.error;
      if (teamsResponse.error) throw teamsResponse.error;

      // Format role options
      const formattedRoles: RoleOption[] = (rolesResponse.data || []).map(role => ({
        label: role.name,
        value: role.name,
        description: `Allow users with ${role.name} role to access this pipeline`
      }));

      // Format user options
      const formattedUsers: UserOption[] = (usersResponse.data || []).map(user => {
        const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
        const displayName = fullName || user.email;
        
        // Handle roles properly (could be array or object)
        let userRole = '';
        if (user.roles) {
          if (Array.isArray(user.roles)) {
            userRole = user.roles[0]?.name || '';
          } else if (typeof user.roles === 'object') {
            userRole = (user.roles as any).name || '';
          }
        }

        return {
          label: displayName,
          value: user.id,
          email: user.email,
          role: userRole
        };
      });

      // Get unique user IDs for managers
      const teamData = teamsResponse.data || [];
      const userIds = new Set<string>();
      teamData.forEach(team => {
        if (team.manager_id) userIds.add(team.manager_id);
        if (team.biz_dev_id) userIds.add(team.biz_dev_id);
      });

      // Fetch manager details if we have any user IDs
      let managerData: UserData[] = [];
      if (userIds.size > 0) {
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('id, first_name, last_name')
          .in('id', Array.from(userIds));
        
        if (userError) {
          console.error('Error fetching manager data:', userError);
        } else {
          managerData = userData || [];
        }
      }

      // Create user map
      const userMap = new Map<string, { first_name: string; last_name: string }>();
      managerData.forEach(user => {
        userMap.set(user.id, { first_name: user.first_name, last_name: user.last_name });
      });

      // Format team options
      const formattedTeams: TeamOption[] = teamData.map(team => {
        let managerName = '';
        if (team.manager_id && userMap.has(team.manager_id)) {
          const manager = userMap.get(team.manager_id)!;
          managerName = [manager.first_name, manager.last_name].filter(Boolean).join(' ');
        }

        return {
          label: team.name,
          value: team.id.toString(),
          type: team.type,
          manager: managerName
        };
      });

      setRoleOptions(formattedRoles);
      setUserOptions(formattedUsers);
      setTeamOptions(formattedTeams);

      // Set current pipeline permissions
      const currentRolePermissions = pipeline.role_permissions || [];
      const currentUserPermissions = pipeline.user_permissions || [];
      const currentTeamPermissions = pipeline.team_permissions || [];
      
      setSelectedRoles(Array.isArray(currentRolePermissions) ? currentRolePermissions : []);
      setSelectedUsers(Array.isArray(currentUserPermissions) ? currentUserPermissions : []);
      setSelectedTeams(Array.isArray(currentTeamPermissions) ? currentTeamPermissions : []);

    } catch (error) {
      console.error('Error loading access data:', error);
      toast({
        title: "Error",
        description: "Failed to load access configuration data",
        variant: "destructive",
      });
    } finally {
      setAccessLoading(false);
    }

    setIsAccessDialogOpen(true);
  };

  const handleSaveAccess = async () => {
    if (!selectedPipelineForAccess) return;
    
    setAccessSaving(true);
    try {
      const { error } = await supabase
        .from('pipelines')
        .update({
          role_permissions: selectedRoles.length > 0 ? selectedRoles : null,
          user_permissions: selectedUsers.length > 0 ? selectedUsers : null,
          team_permissions: selectedTeams.length > 0 ? selectedTeams : null,
        })
        .eq('id', selectedPipelineForAccess.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Pipeline access permissions updated successfully",
      });

      // Refresh pipelines to reflect changes
      await fetchPipelines();
      setIsAccessDialogOpen(false);
      
    } catch (error: any) {
      console.error('Error saving access permissions:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save access permissions",
        variant: "destructive",
      });
    } finally {
      setAccessSaving(false);
    }
  };

  // Load Template handler
  const handleOpenLoadTemplateDialog = async (pipeline: Pipeline) => {
    setPipelineToLoadTemplate(pipeline);
    setSelectedTemplateToLoad(null);

    // Fetch available templates matching the pipeline type
    try {
      const { data: templates, error } = await supabase
        .from('pipeline_templates_library')
        .select('id, name, description, scope, pipeline_type, template_version')
        .eq('pipeline_type', pipeline.pipeline_type)
        .order('name');

      if (error) throw error;
      setAvailableTemplates(templates || []);
      setIsLoadTemplateDialogOpen(true);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast({
        title: "Error",
        description: "Failed to load templates",
        variant: "destructive"
      });
    }
  };

  // Helper function to remap conditional logic field IDs from template IDs to database IDs
  const remapConditionalLogicIds = (conditionalLogic: any, itemIdMap: Map<string, number>): any => {
    if (!conditionalLogic || !Array.isArray(conditionalLogic)) {
      return conditionalLogic;
    }

    // Conditional logic is an array of LogicGroup objects
    return conditionalLogic.map((group: any) => {
      if (!group.rules || !Array.isArray(group.rules)) {
        return group;
      }

      // Remap field IDs in each rule
      const remappedRules = group.rules.map((rule: any) => {
        if (!rule.field) {
          return rule;
        }

        // Check if this field ID exists in our mapping
        // Try both formats: "535" and "item-535"
        const templateFieldId = String(rule.field);
        let dbFieldId = itemIdMap.get(templateFieldId);

        // If not found, try with "item-" prefix
        if (dbFieldId === undefined && !templateFieldId.startsWith('item-')) {
          dbFieldId = itemIdMap.get(`item-${templateFieldId}`);
        }

        if (dbFieldId !== undefined) {
          // Replace template field ID with database field ID
          return {
            ...rule,
            field: String(dbFieldId), // Keep as string for consistency
          };
        }

        // If no mapping found, return rule as-is (might be a project field with negative ID)
        return rule;
      });

      return {
        ...group,
        rules: remappedRules,
      };
    });
  };

  const handleLoadTemplate = async () => {
    if (!pipelineToLoadTemplate || !selectedTemplateToLoad) return;

    setIsLoadingTemplate(true);
    try {
      // Fetch template data with stages
      const { data: template, error: templateError } = await supabase
        .from('pipeline_templates_library')
        .select('*')
        .eq('id', selectedTemplateToLoad)
        .single();

      if (templateError) throw templateError;

      // IMPORTANT: Handle dependencies in the correct order to avoid foreign key violations
      // Strategy: Create new stages first, migrate projects to them, then delete old stages

      // 1. Get all existing stages (excluding temp stages from previous failed runs)
      const { data: allStages, error: stagesQueryError } = await supabase
        .from('stages')
        .select('id, name, stage_order')
        .eq('pipeline_id', pipelineToLoadTemplate.id)
        .order('stage_order');

      if (stagesQueryError) {
        console.error('Error fetching existing stages:', stagesQueryError);
        throw new Error(`Failed to fetch existing stages: ${stagesQueryError.message}`);
      }

      // Separate real stages from temp/archived stages (temp stages have negative order or temp names)
      const existingStages = allStages?.filter(s =>
        s.stage_order >= 0 && !s.name.startsWith('_temp_') && !s.name.startsWith('_archived_')
      ) || [];

      const tempStages = allStages?.filter(s =>
        s.stage_order < 0 || s.name.startsWith('_temp_')
      ) || [];

      console.log(`Found ${existingStages.length} real stages and ${tempStages.length} temp stages from previous runs`);

      // Clean up temp stages from previous failed runs FIRST
      if (tempStages.length > 0) {
        console.log(`Cleaning up ${tempStages.length} temp stages from previous runs...`);

        // Check if any projects reference these temp stages
        const tempStageIds = tempStages.map(s => s.id);
        const { data: projectsOnTempStages, error: tempCheckError } = await supabase
          .from('projects')
          .select('id, current_stage_id')
          .in('current_stage_id', tempStageIds);

        if (!tempCheckError && projectsOnTempStages && projectsOnTempStages.length > 0) {
          console.log(`Found ${projectsOnTempStages.length} projects on temp stages, migrating to first real stage...`);

          // Move these projects to the first real stage (or create a placeholder)
          if (existingStages.length > 0) {
            const firstRealStageId = existingStages[0].id;
            await supabase
              .from('projects')
              .update({ current_stage_id: firstRealStageId })
              .in('id', projectsOnTempStages.map(p => p.id));
            console.log(`✓ Migrated ${projectsOnTempStages.length} projects to stage ${firstRealStageId}`);
          }
        }

        // Now delete temp stages
        const { error: deleteTempError } = await supabase
          .from('stages')
          .delete()
          .in('id', tempStageIds);

        if (deleteTempError) {
          console.warn('Could not delete all temp stages:', deleteTempError);
          // Continue anyway - we'll use a different offset
        } else {
          console.log(`✓ Cleaned up ${tempStages.length} temp stages`);
        }
      }

      // Create a mapping of old stage info (only real stages now that temps are cleaned)
      const oldStageMap = new Map(existingStages?.map(s => [s.id, { name: s.name, order: s.stage_order }]) || []);

      // 2. Get all projects in this pipeline and their current stages
      const { data: projectsInPipeline, error: projectsError } = await supabase
        .from('projects')
        .select('id, current_stage_id')
        .eq('pipeline_id', pipelineToLoadTemplate.id);

      if (projectsError) {
        console.error('Error fetching projects:', projectsError);
        throw new Error(`Failed to fetch projects: ${projectsError.message}`);
      }

      // 3. Create NEW stages from template (with temporary names to avoid unique constraint)
      const tenantId = await getCurrentTenantId();
      let newStages: any[] = [];

      if (template.stages && template.stages.length > 0) {
        // Use negative offsets for stage_order to avoid conflicts (stages normally start at 0+)
        const stagesToInsert = template.stages.map((stage: any, index: number) => ({
          pipeline_id: pipelineToLoadTemplate.id,
          name: `_temp_${Date.now()}_${index}_${stage.name}`, // Temporary unique name
          description: stage.description || null,
          stage_order: -1000 - index, // Negative offset to avoid conflicts with existing positive orders
          sla_enabled: stage.sla_enabled || false,
          sla_settings: stage.sla_settings || null,
          permissions: stage.permissions || null,
          user_permissions: stage.user_permissions || [],
          tenant_id: tenantId,
        }));

        const { data: insertedStages, error: insertError } = await supabase
          .from('stages')
          .insert(stagesToInsert)
          .select('id, name, stage_order');

        if (insertError) {
          console.error('Error inserting new stages:', insertError);
          throw new Error(`Failed to insert new stages: ${insertError.message}`);
        }

        newStages = insertedStages || [];

        // 4. Update all projects to point to the new stages
        if (projectsInPipeline && projectsInPipeline.length > 0 && newStages.length > 0) {
          console.log(`Migrating ${projectsInPipeline.length} projects to new stages...`);

          // Create mapping by extracting real names from temp names
          const newStagesByRealName = new Map();
          const newStagesByTempOrder = new Map();

          template.stages.forEach((templateStage: any, index: number) => {
            if (newStages[index]) {
              newStagesByRealName.set(templateStage.name.toLowerCase().trim(), newStages[index].id);
              newStagesByTempOrder.set(templateStage.stage_order, newStages[index].id);
            }
          });

          // Create old stage ID to new stage ID mapping
          const stageIdMapping = new Map();

          console.log('Creating stage ID mapping...');
          console.log('Template stages:', template.stages.map((s: any) => `${s.name} (order ${s.stage_order})`));
          console.log('Old stages:', Array.from(oldStageMap.entries()).map(([id, info]) => `${id}: ${info.name} (order ${info.order})`));
          console.log('New stages:', newStages.map(s => `${s.id}: ${s.name} (order ${s.stage_order})`));

          for (const [oldStageId, oldStageInfo] of oldStageMap.entries()) {
            let newStageId = null;
            let matchMethod = 'none';

            // Try to match by name first
            newStageId = newStagesByRealName.get(oldStageInfo.name.toLowerCase().trim());
            if (newStageId) matchMethod = 'name';

            // If no name match, try to match by order
            if (!newStageId) {
              newStageId = newStagesByTempOrder.get(oldStageInfo.order);
              if (newStageId) matchMethod = 'order';
            }

            // If still no match, use the first new stage as fallback
            if (!newStageId && newStages.length > 0) {
              newStageId = newStages[0].id;
              matchMethod = 'fallback';
            }

            if (newStageId) {
              stageIdMapping.set(oldStageId, newStageId);
              console.log(`Mapped old stage ${oldStageId} (${oldStageInfo.name}) → new stage ${newStageId} (${matchMethod})`);
            }
          }

          // Batch update projects by new stage ID
          const projectsByNewStage = new Map();
          console.log('Grouping projects for migration:');
          for (const project of projectsInPipeline) {
            if (!project.current_stage_id) continue;

            const newStageId = stageIdMapping.get(project.current_stage_id) || newStages[0].id;
            const oldStageInfo = oldStageMap.get(project.current_stage_id);
            console.log(`  Project ${project.id}: stage ${project.current_stage_id} (${oldStageInfo?.name || 'unknown'}) → ${newStageId}`);

            if (!projectsByNewStage.has(newStageId)) {
              projectsByNewStage.set(newStageId, []);
            }
            projectsByNewStage.get(newStageId).push(project.id);
          }

          // Execute batch updates
          console.log(`Executing batch updates for ${projectsByNewStage.size} target stages...`);
          for (const [newStageId, projectIds] of projectsByNewStage.entries()) {
            console.log(`Updating ${projectIds.length} projects to stage ${newStageId}:`, projectIds);
            const { error: updateError } = await supabase
              .from('projects')
              .update({ current_stage_id: newStageId })
              .in('id', projectIds);

            if (updateError) {
              console.error(`Error updating ${projectIds.length} projects to stage ${newStageId}:`, updateError);
              throw new Error(`Failed to update projects: ${updateError.message}`);
            }
            console.log(`✓ Updated ${projectIds.length} projects to new stage ${newStageId}`);
          }

          // Verify all projects are updated (check against real stages only, temps already cleaned)
          console.log('Verifying project migration...');
          const oldRealStageIds = existingStages.map(s => s.id);

          const { data: remainingProjects, error: verifyError } = await supabase
            .from('projects')
            .select('id, current_stage_id, project_name')
            .eq('pipeline_id', pipelineToLoadTemplate.id)
            .in('current_stage_id', oldRealStageIds);

          if (!verifyError && remainingProjects && remainingProjects.length > 0) {
            console.error(`⚠️  WARNING: ${remainingProjects.length} projects still reference old stages after migration!`);
            remainingProjects.forEach(p => {
              const oldStageInfo = oldStageMap.get(p.current_stage_id);
              console.error(`  - Project ${p.id} (${p.project_name}): still on old stage ${p.current_stage_id} (${oldStageInfo?.name})`);
            });

            // Try to map them properly
            console.log('Attempting to migrate remaining projects...');
            for (const project of remainingProjects) {
              const newStageId = stageIdMapping.get(project.current_stage_id) || newStages[0].id;
              console.log(`  Migrating project ${project.id} from ${project.current_stage_id} to ${newStageId}`);

              const { error: individualUpdateError } = await supabase
                .from('projects')
                .update({ current_stage_id: newStageId })
                .eq('id', project.id);

              if (individualUpdateError) {
                console.error(`    Failed: ${individualUpdateError.message}`);
              } else {
                console.log(`    ✓ Success`);
              }
            }

            // Verify again
            const { data: stillRemaining } = await supabase
              .from('projects')
              .select('id, current_stage_id')
              .eq('pipeline_id', pipelineToLoadTemplate.id)
              .in('current_stage_id', oldRealStageIds);

            if (stillRemaining && stillRemaining.length > 0) {
              throw new Error(`Failed to migrate ${stillRemaining.length} projects after retry. Cannot proceed with template load.`);
            }
            console.log(`✓ All remaining projects successfully migrated`);
          } else {
            console.log('✓ All projects successfully migrated on first pass');
          }
        }
      }

      // 5. Now delete OLD checklist templates and related data
      const { data: existingChecklists, error: checklistsQueryError } = await supabase
        .from('checklist_templates')
        .select('id')
        .eq('pipeline_id', pipelineToLoadTemplate.id);

      if (!checklistsQueryError && existingChecklists && existingChecklists.length > 0) {
        const checklistIds = existingChecklists.map(c => c.id);

        await supabase
          .from('checklist_items')
          .delete()
          .in('template_id', checklistIds);

        await supabase
          .from('checklist_groups')
          .delete()
          .in('template_id', checklistIds);

        await supabase
          .from('checklist_templates')
          .delete()
          .eq('pipeline_id', pipelineToLoadTemplate.id);
      }

      // 6. Delete OLD real stages (temp stages already cleaned up earlier)
      if (existingStages.length > 0) {
        const oldStageIds = existingStages.map(s => s.id);
        console.log(`Attempting to delete ${existingStages.length} old real stages:`, oldStageIds);

        // Double-check: verify NO projects (from ANY pipeline) reference these old stages
        const { data: stillReferencedProjects, error: checkError } = await supabase
          .from('projects')
          .select('id, current_stage_id, pipeline_id, project_name')
          .in('current_stage_id', oldStageIds);

        console.log(`Checking for ANY projects referencing old stages...`);

        // Wait a moment for database to commit the updates
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Re-query to force fresh data
        const { data: freshCheck, error: freshCheckError } = await supabase
          .from('projects')
          .select('id, current_stage_id, pipeline_id, project_name')
          .in('current_stage_id', oldStageIds);

        console.log(`Fresh check result: ${freshCheck?.length || 0} projects still reference old stages`);

        if (checkError || freshCheckError) {
          console.error('Error checking for referenced projects:', checkError || freshCheckError);
        } else if ((stillReferencedProjects && stillReferencedProjects.length > 0) || (freshCheck && freshCheck.length > 0)) {
          const projectsStillReferencing = freshCheck && freshCheck.length > 0 ? freshCheck : stillReferencedProjects;
          console.error(`CRITICAL: ${projectsStillReferencing.length} projects still reference old stages!`);

          // Analyze what's blocking deletion
          const projectsByPipeline = new Map();
          projectsStillReferencing.forEach(p => {
            if (!projectsByPipeline.has(p.pipeline_id)) {
              projectsByPipeline.set(p.pipeline_id, []);
            }
            projectsByPipeline.get(p.pipeline_id).push(p);
          });

          console.error('Projects blocking deletion by pipeline:');
          for (const [pipelineId, projects] of projectsByPipeline.entries()) {
            const isCurrentPipeline = pipelineId === pipelineToLoadTemplate.id;
            console.error(`  Pipeline ${pipelineId} ${isCurrentPipeline ? '(CURRENT - THIS IS BAD!)' : '(different pipeline - data integrity issue)'}: ${projects.length} projects`);
            projects.forEach((p: any) => {
              console.error(`    - ${p.project_name} (${p.id}): stage ${p.current_stage_id}`);
            });
          }

          // Group by stage ID to see which stages are still referenced
          const referencedStageIds = new Set(projectsStillReferencing.map(p => p.current_stage_id));
          console.error('Stages still referenced:', Array.from(referencedStageIds));

          // Only delete stages that are NOT referenced
          const safeToDeleteIds = oldStageIds.filter(id => !referencedStageIds.has(id));

          if (safeToDeleteIds.length > 0) {
            console.log(`Deleting ${safeToDeleteIds.length} unreferenced stages:`, safeToDeleteIds);
            const { error: deleteError } = await supabase
              .from('stages')
              .delete()
              .in('id', safeToDeleteIds);

            if (deleteError) {
              console.error('Error deleting safe stages:', deleteError);
            } else {
              console.log(`✓ Deleted ${safeToDeleteIds.length} safe stages`);
            }
          }

          throw new Error(`Cannot delete ${referencedStageIds.size} stages that are still referenced by ${projectsStillReferencing.length} projects. Check console for details.`);
        } else {
          console.log('✓ No projects reference old stages - attempting deletion...');
          // Safe to delete all
          const { error: deleteStagesError } = await supabase
            .from('stages')
            .delete()
            .in('id', oldStageIds);

          if (deleteStagesError) {
            console.warn('⚠️  Could not delete old stages (foreign key constraint - possible RLS or trigger issue):', deleteStagesError);
            console.warn('Proceeding anyway - old stages will remain in database but are unused');
            console.warn('You may need to manually clean up stages:', oldStageIds);
            // Don't throw - continue with template load
          } else {
            console.log(`✓ Successfully deleted ${oldStageIds.length} old stages`);
          }
        }
      } else {
        console.log('No old real stages to delete');
      }

      // 7. Rename stages: old stages to "_archived_" prefix, new stages to proper names
      if (newStages.length > 0 && template.stages) {
        console.log('Renaming stages...');

        // Step 7a: Rename old stages to free up the names (if they still exist)
        if (existingStages.length > 0) {
          console.log(`Renaming ${existingStages.length} old stages to _archived_ prefix...`);
          // Use timestamp-based offset to ensure uniqueness even with multiple archived sets
          const archiveTimestamp = Date.now();
          const archiveOffset = Math.floor(archiveTimestamp / 1000); // Use seconds for smaller numbers

          for (let i = 0; i < existingStages.length; i++) {
            const oldStage = existingStages[i];
            const { error: renameOldError } = await supabase
              .from('stages')
              .update({
                name: `_archived_${archiveTimestamp}_${oldStage.name}`,
                stage_order: archiveOffset + i, // Unique order based on timestamp + index
              })
              .eq('id', oldStage.id);

            if (renameOldError) {
              console.error(`Could not rename old stage ${oldStage.id}:`, renameOldError);
              throw new Error(`Failed to archive old stages: ${renameOldError.message}`);
            }
          }
          console.log('✓ Renamed old stages');
        }

        // Step 7b: Now rename new stages to their proper names
        console.log('Renaming new temp stages to proper names...');
        for (let i = 0; i < newStages.length; i++) {
          const newStage = newStages[i];
          const templateStage = template.stages[i];

          const { error: renameError } = await supabase
            .from('stages')
            .update({
              name: templateStage.name,
              stage_order: templateStage.stage_order,
            })
            .eq('id', newStage.id);

          if (renameError) {
            console.error(`Failed to rename stage ${newStage.id} to "${templateStage.name}":`, renameError);
            throw new Error(`Failed to rename stages: ${renameError.message}`);
          }
        }
        console.log('✓ Renamed all new stages to proper names');
      }

      // Load checklist templates associated with this pipeline template
      const { data: checklistTemplates, error: checklistError } = await supabase
        .from('checklist_templates_library')
        .select('*')
        .eq('pipeline_template_id', template.id)
        .order('display_order', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: true });

      if (!checklistError && checklistTemplates && checklistTemplates.length > 0) {
        console.log(`Loading ${checklistTemplates.length} checklist templates from pipeline template...`);

        // Sort checklist templates by display_order to maintain order
        const sortedChecklistTemplates = [...checklistTemplates].sort((a, b) =>
          (a.display_order || 0) - (b.display_order || 0)
        );

        // Get the newly created stages to map checklist templates to actual stage IDs
        const { data: newStages, error: stagesQueryError } = await supabase
          .from('stages')
          .select('id, name, stage_order')
          .eq('pipeline_id', pipelineToLoadTemplate.id)
          .order('stage_order');

        if (!stagesQueryError && newStages) {
          const tenantId = await getCurrentTenantId();

          for (let checklistIndex = 0; checklistIndex < sortedChecklistTemplates.length; checklistIndex++) {
            const checklistTemplate = sortedChecklistTemplates[checklistIndex];
            console.log(`Creating checklist: ${checklistTemplate.name} (display_order: ${checklistTemplate.display_order ?? checklistIndex})`);

            // Determine checklist_type from pipeline type
            const checklistType = pipelineToLoadTemplate.pipeline_type === 'commercial' ? 'commercial' : 'residential';

            // Try to match stage by name if stage_name is provided
            let matchedStageId = null;
            if (checklistTemplate.stage_name && newStages) {
              const matchedStage = newStages.find((stage: any) =>
                stage.name.toLowerCase().trim() === checklistTemplate.stage_name.toLowerCase().trim()
              );
              if (matchedStage) {
                matchedStageId = matchedStage.id;
                console.log(`  Matched to stage: ${matchedStage.name}`);
              } else {
                console.log(`  Could not match stage: ${checklistTemplate.stage_name}`);
              }
            }

            // Create the checklist template entry
            // Use template's display_order if available, otherwise use loop index to maintain order
            const { data: createdChecklist, error: insertError } = await supabase
              .from('checklist_templates')
              .insert({
                name: checklistTemplate.name,
                description: checklistTemplate.description || '',
                checklist_type: checklistType,
                pipeline_id: pipelineToLoadTemplate.id,
                tenant_id: tenantId,
                stage_id: matchedStageId,
                display_order: checklistTemplate.display_order ?? checklistIndex,
              })
              .select()
              .single();

            if (!insertError && createdChecklist) {
              // Copy items and groups from the template configuration (JSONB)
              const config = checklistTemplate.configuration;

              console.log(`  Configuration structure:`, {
                hasItems: !!config.items,
                itemsIsArray: Array.isArray(config.items),
                itemsCount: config.items?.length || 0,
                hasGroups: !!config.groups,
                groupsIsArray: Array.isArray(config.groups),
                groupsCount: config.groups?.length || 0,
                hasUngroupedItems: !!config.ungrouped_items,
                ungroupedItemsCount: config.ungrouped_items?.length || 0,
              });

              // Handle both possible configuration structures
              // Priority: Check for groups FIRST (preserves grouping structure)
              // Fallback: Use flat items array if no groups exist

              if (config.groups && Array.isArray(config.groups) && config.groups.length > 0) {
                // Step 1: Create all groups and build ID map (template ID → database ID)
                console.log(`Creating ${config.groups.length} groups...`);
                const groupIdMap = new Map<string, number>(); // template group.id → database group.id

                for (const group of config.groups) {
                  const { data: createdGroup, error: createGroupError } = await supabase
                    .from('checklist_groups')
                    .insert({
                      template_id: createdChecklist.id,
                      group_name: group.group_name,
                      group_order: group.group_order,
                      permissions: group.permissions || null,
                      user_permissions: group.user_permissions || null,
                      conditional_logic: group.conditional_logic || null,
                      tenant_id: tenantId,
                    })
                    .select()
                    .single();

                  if (createGroupError) {
                    console.error(`Error creating group ${group.group_name}:`, createGroupError);
                  } else if (createdGroup && group.id) {
                    groupIdMap.set(group.id, createdGroup.id);
                    console.log(`  ✓ Created group: ${group.group_name} (${group.id} → ${createdGroup.id})`);
                  }
                }

                console.log(`✓ Created ${groupIdMap.size} groups`);

                // Step 2: Process all items from config.items, matching them to groups
                if (config.items && Array.isArray(config.items) && config.items.length > 0) {
                  console.log(`Processing ${config.items.length} items and matching to groups...`);

                  const itemsToInsert = config.items.map((item: any) => {
                    // Look up the database group ID if item has a template group_id
                    let dbGroupId = null;
                    if (item.group_id && typeof item.group_id === 'string') {
                      dbGroupId = groupIdMap.get(item.group_id) || null;
                    }

                    return {
                      template_id: createdChecklist.id,
                      group_id: dbGroupId,
                      item_name: item.name || item.item_name || item.item_text || '',
                      item_order: item.item_order || item.order || item.display_order || 0,
                      description: item.description || item.item_description || null,
                      item_type: item.item_type || item.type || 'CHECKBOX',
                      is_required: item.is_required !== undefined ? item.is_required : false,
                      item_category: item.item_category || item.category || 'project',
                      item_values: item.item_values || item.options || null,
                      permissions: item.permissions || null,
                      user_permissions: item.user_permissions || null,
                      conditional_logic: null, // Will update after ID mapping
                      tenant_id: tenantId,
                    };
                  });

                  const groupedCount = itemsToInsert.filter((i: any) => i.group_id !== null).length;
                  const ungroupedCount = itemsToInsert.filter((i: any) => i.group_id === null).length;
                  console.log(`  ${groupedCount} grouped items, ${ungroupedCount} ungrouped items`);

                  // Insert items and get their new IDs
                  const { data: insertedItems, error: itemInsertError } = await supabase
                    .from('checklist_items')
                    .insert(itemsToInsert)
                    .select('id');

                  if (itemInsertError) {
                    console.error(`Error inserting items:`, itemInsertError);
                  } else if (insertedItems) {
                    console.log(`✓ Inserted ${itemsToInsert.length} items total`);

                    // Step 3: Build item ID map (template ID → database ID)
                    const itemIdMap = new Map<string, number>();
                    config.items.forEach((templateItem: any, index: number) => {
                      if (templateItem.id && insertedItems[index]) {
                        const templateId = String(templateItem.id); // Convert to string for consistent lookup
                        const dbId = insertedItems[index].id;
                        itemIdMap.set(templateId, dbId);
                      }
                    });

                    console.log(`✓ Built item ID map with ${itemIdMap.size} entries`);

                    // Step 4: Update conditional_logic on items that have it
                    const itemUpdates: any[] = [];
                    config.items.forEach((templateItem: any, index: number) => {
                      if (templateItem.conditional_logic && insertedItems[index]) {
                        const remappedLogic = remapConditionalLogicIds(templateItem.conditional_logic, itemIdMap);
                        itemUpdates.push({
                          id: insertedItems[index].id,
                          conditional_logic: remappedLogic,
                        });
                      }
                    });

                    if (itemUpdates.length > 0) {
                      console.log(`Updating conditional_logic on ${itemUpdates.length} items...`);
                      for (const update of itemUpdates) {
                        await supabase
                          .from('checklist_items')
                          .update({ conditional_logic: update.conditional_logic })
                          .eq('id', update.id);
                      }
                      console.log(`✓ Updated conditional_logic on items`);
                    }

                    // Step 5: Update conditional_logic on groups that have it
                    const groupUpdates: any[] = [];
                    config.groups?.forEach((templateGroup: any) => {
                      if (templateGroup.conditional_logic && templateGroup.id) {
                        const dbGroupId = groupIdMap.get(templateGroup.id);
                        if (dbGroupId) {
                          const remappedLogic = remapConditionalLogicIds(templateGroup.conditional_logic, itemIdMap);
                          groupUpdates.push({
                            id: dbGroupId,
                            conditional_logic: remappedLogic,
                          });
                        }
                      }
                    });

                    if (groupUpdates.length > 0) {
                      console.log(`Updating conditional_logic on ${groupUpdates.length} groups...`);
                      for (const update of groupUpdates) {
                        await supabase
                          .from('checklist_groups')
                          .update({ conditional_logic: update.conditional_logic })
                          .eq('id', update.id);
                      }
                      console.log(`✓ Updated conditional_logic on groups`);
                    }
                  }
                }
              } else if (config.items && Array.isArray(config.items) && config.items.length > 0) {
                // Fallback: Flat items array (no groups structure)
                console.log(`Loading ${config.items.length} items (no groups)...`);
                const itemsToInsert = config.items.map((item: any, index: number) => ({
                  template_id: createdChecklist.id,
                  group_id: null,
                  item_name: item.name || item.item_name || item.item_text || `Item ${index + 1}`,
                  item_order: item.order || item.item_order || item.display_order || index,
                  description: item.description || item.item_description || null,
                  item_type: item.item_type || item.type || 'CHECKBOX',
                  is_required: item.is_required !== undefined ? item.is_required : false,
                  item_category: item.item_category || item.category || 'project',
                  item_values: item.item_values || item.options || null,
                  permissions: item.permissions || null,
                  user_permissions: item.user_permissions || null,
                  conditional_logic: item.conditional_logic || null,
                  tenant_id: tenantId,
                }));

                if (itemsToInsert.length > 0) {
                  const { error: itemsInsertError } = await supabase
                    .from('checklist_items')
                    .insert(itemsToInsert);

                  if (itemsInsertError) {
                    console.error('Error inserting flat items:', itemsInsertError);
                  } else {
                    console.log(`✓ Inserted ${itemsToInsert.length} flat items`);
                  }
                }
              }

              // Note: ungrouped_items are now handled within config.items above (items with group_id: null)
            }
          }
        }
      }

      // Update pipeline to link to template
      await supabase
        .from('pipelines')
        .update({
          template_id: template.id,
          template_version: template.template_version,
          last_template_sync: new Date().toISOString()
        })
        .eq('id', pipelineToLoadTemplate.id);

      toast({
        title: "Success",
        description: `Template loaded successfully${checklistTemplates?.length ? ` with ${checklistTemplates.length} checklist(s)` : ''}`
      });

      await fetchPipelines();
      setIsLoadTemplateDialogOpen(false);
      setPipelineToLoadTemplate(null);
      setSelectedTemplateToLoad(null);
    } catch (error: any) {
      console.error('Error loading template:', error);
      toast({
        title: "Error",
        description: "Failed to load template",
        variant: "destructive"
      });
    } finally {
      setIsLoadingTemplate(false);
    }
  };

  // Sync Template handler
  const handleOpenSyncConfirmation = async (pipeline: Pipeline) => {
    setPipelineToSync(pipeline);
    setSyncDirection('template-to-pipeline');
    setIsLoadingComparison(true);
    setIsSyncConfirmationOpen(true);

    try {
      // Fetch pipeline stages
      const { data: pipelineStagesData, error: pipelineError } = await supabase
        .from('stages')
        .select('*')
        .eq('pipeline_id', pipeline.id)
        .order('stage_order', { ascending: true });

      if (pipelineError) throw pipelineError;
      setPipelineStages(pipelineStagesData || []);

      // Fetch pipeline checklists with full details
      const { data: pipelineChecklists, error: pipelineChecklistsError } = await supabase
        .from('checklist_templates')
        .select(`
          *,
          items:checklist_items(*, group:checklist_groups(*)),
          groups:checklist_groups(*, items:checklist_items(*))
        `)
        .eq('pipeline_id', pipeline.id);

      if (pipelineChecklistsError) throw pipelineChecklistsError;

      // Fetch template data (stages + checklists)
      const { data: template, error: templateError } = await supabase
        .from('pipeline_templates_library')
        .select('stages')
        .eq('id', pipeline.template_id)
        .single();

      if (templateError) throw templateError;
      setTemplateStages(template?.stages || []);

      // Fetch template checklists
      const { data: templateChecklists, error: templateChecklistsError } = await supabase
        .from('checklist_templates_library')
        .select('*')
        .eq('pipeline_template_id', pipeline.template_id);

      if (templateChecklistsError) throw templateChecklistsError;

      // Store comparison data in state (we'll need new state variables)
      setPipelineChecklistsData(pipelineChecklists || []);
      setTemplateChecklistsData(templateChecklists || []);

    } catch (error) {
      console.error('Error loading comparison data:', error);
      toast({
        title: "Error",
        description: "Failed to load comparison data",
        variant: "destructive"
      });
    } finally {
      setIsLoadingComparison(false);
    }
  };

  const handleSyncWithTemplate = async () => {
    if (!pipelineToSync || !pipelineToSync.template_id) return;

    setIsLoadingTemplate(true);
    try {
      const { data: template, error: templateError } = await supabase
        .from('pipeline_templates_library')
        .select('*')
        .eq('id', pipelineToSync.template_id)
        .single();

      if (templateError) throw templateError;

      if (syncDirection === 'template-to-pipeline') {
        // Sync FROM template TO pipeline
        const tenantId = await getCurrentTenantId();

        // Delete existing stages and checklists
        await supabase
          .from('checklist_templates')
          .delete()
          .eq('pipeline_id', pipelineToSync.id);

        await supabase
          .from('stages')
          .delete()
          .eq('pipeline_id', pipelineToSync.id);

        // Insert new stages from template
        if (template.stages && template.stages.length > 0) {
          const stagesToInsert = template.stages.map((stage: any) => ({
            pipeline_id: pipelineToSync.id,
            name: stage.name,
            description: stage.description || null,
            stage_order: stage.stage_order,
            sla_enabled: stage.sla_enabled || false,
            sla_settings: stage.sla_settings || null,
            permissions: stage.permissions || null,
            user_permissions: stage.user_permissions || [],
            tenant_id: tenantId,
          }));

          const { error: stagesError } = await supabase
            .from('stages')
            .insert(stagesToInsert);

          if (stagesError) throw stagesError;
        }

        // Load checklist templates from the template library
        const { data: checklistTemplates } = await supabase
          .from('checklist_templates_library')
          .select('*')
          .eq('pipeline_template_id', template.id);

        if (checklistTemplates && checklistTemplates.length > 0) {
          // Get newly created stages for matching
          const { data: newStages } = await supabase
            .from('stages')
            .select('id, name, stage_order')
            .eq('pipeline_id', pipelineToSync.id)
            .order('stage_order');

          for (const checklistTemplate of checklistTemplates) {
            const checklistType = pipelineToSync.pipeline_type === 'commercial' ? 'commercial' : 'residential';

            // Match stage by name
            let matchedStageId = null;
            if (checklistTemplate.stage_name && newStages) {
              const matchedStage = newStages.find((stage: any) =>
                stage.name.toLowerCase().trim() === checklistTemplate.stage_name.toLowerCase().trim()
              );
              if (matchedStage) {
                matchedStageId = matchedStage.id;
              }
            }

            // Create checklist
            const { data: createdChecklist } = await supabase
              .from('checklist_templates')
              .insert({
                name: checklistTemplate.name,
                description: checklistTemplate.description || '',
                checklist_type: checklistType,
                pipeline_id: pipelineToSync.id,
                tenant_id: tenantId,
                stage_id: matchedStageId,
                display_order: checklistTemplate.display_order || 0,
              })
              .select()
              .single();

            // Copy checklist items from configuration
            if (createdChecklist && checklistTemplate.configuration) {
              const config = checklistTemplate.configuration;

              if (config.items && Array.isArray(config.items) && config.items.length > 0) {
                const itemsToInsert = config.items.map((item: any, index: number) => ({
                  template_id: createdChecklist.id,
                  group_id: null,
                  item_name: item.name || item.item_name || item.item_text || `Item ${index + 1}`,
                  item_order: item.order || item.item_order || index,
                  tenant_id: tenantId,
                }));

                if (itemsToInsert.length > 0) {
                  await supabase.from('checklist_items').insert(itemsToInsert);
                }
              }

              if (config.groups && Array.isArray(config.groups)) {
                for (const group of config.groups) {
                  const { data: createdGroup } = await supabase
                    .from('checklist_groups')
                    .insert({
                      template_id: createdChecklist.id,
                      group_name: group.group_name,
                      group_order: group.group_order,
                      tenant_id: tenantId,
                    })
                    .select()
                    .single();

                  if (createdGroup && group.items && Array.isArray(group.items)) {
                    const itemsToInsert = group.items.map((item: any) => ({
                      template_id: createdChecklist.id,
                      group_id: createdGroup.id,
                      item_name: item.name || item.item_name || item.item_text || '',
                      item_order: item.item_order || item.order || 0,
                      tenant_id: tenantId,
                    }));

                    await supabase.from('checklist_items').insert(itemsToInsert);
                  }
                }
              }

              if (config.ungrouped_items && Array.isArray(config.ungrouped_items)) {
                const ungroupedItemsToInsert = config.ungrouped_items.map((item: any) => ({
                  template_id: createdChecklist.id,
                  group_id: null,
                  item_name: item.name || item.item_name || item.item_text || '',
                  item_order: item.item_order || item.order || 0,
                  tenant_id: tenantId,
                }));

                await supabase.from('checklist_items').insert(ungroupedItemsToInsert);
              }
            }
          }
        }

        // Update pipeline version tracking
        await supabase
          .from('pipelines')
          .update({
            template_version: template.template_version,
            last_template_sync: new Date().toISOString()
          })
          .eq('id', pipelineToSync.id);

        toast({
          title: "Success",
          description: `Pipeline synced successfully${checklistTemplates?.length ? ` with ${checklistTemplates.length} checklist(s)` : ''}`
        });

      } else if (syncDirection === 'pipeline-to-template') {
        // Sync FROM pipeline TO template
        const { data: currentStages, error: stagesError } = await supabase
          .from('stages')
          .select('*')
          .eq('pipeline_id', pipelineToSync.id)
          .order('stage_order', { ascending: true });

        if (stagesError) throw stagesError;

        const templateStagesData = currentStages.map((stage: any) => ({
          name: stage.name,
          description: stage.description || null,
          stage_order: stage.stage_order,
          sla_enabled: stage.sla_enabled || false,
          sla_settings: stage.sla_settings || null,
          permissions: stage.permissions || null,
          user_permissions: stage.user_permissions || [],
        }));

        const { error: updateError } = await supabase
          .from('pipeline_templates_library')
          .update({
            stages: templateStagesData,
            updated_at: new Date().toISOString()
          })
          .eq('id', pipelineToSync.template_id);

        if (updateError) throw updateError;

        // Fetch updated template version
        const { data: updatedTemplate } = await supabase
          .from('pipeline_templates_library')
          .select('template_version')
          .eq('id', pipelineToSync.template_id)
          .single();

        await supabase
          .from('pipelines')
          .update({
            template_version: updatedTemplate?.template_version || template.template_version + 1,
            last_template_sync: new Date().toISOString()
          })
          .eq('id', pipelineToSync.id);

        toast({
          title: "Success",
          description: "Template updated with pipeline changes"
        });
      }

      // Use flushSync to force immediate synchronous state updates
      flushSync(() => {
        setIsLoadingTemplate(false);
        setIsSyncConfirmationOpen(false);
        setPipelineToSync(null);
        setPipelineStages([]);
        setTemplateStages([]);
        setPipelineChecklistsData([]);
        setTemplateChecklistsData([]);
      });

      // Force cleanup of any stuck dialog overlays/body styles
      setTimeout(() => {
        // Remove any stuck pointer-events or overflow styles on body
        document.body.style.pointerEvents = '';
        document.body.style.overflow = '';

        // Remove any stuck dialog overlays (Radix UI portals)
        const dialogs = document.querySelectorAll('[data-radix-dialog-overlay]');
        dialogs.forEach(el => el.remove());
      }, 50);

      // Fetch pipelines after dialog is closed
      fetchPipelines();
    } catch (error: any) {
      console.error('Error syncing:', error);
      toast({
        title: "Error",
        description: "Failed to sync",
        variant: "destructive"
      });

      // Still close the dialog even on error
      flushSync(() => {
        setIsLoadingTemplate(false);
        setIsSyncConfirmationOpen(false);
        setPipelineToSync(null);
        setPipelineStages([]);
        setTemplateStages([]);
      });
    }
  };

  // Filter pipelines by activeType
  const filteredPipelines = pipelines.filter(p => p.pipeline_type === activeType);

  const handleAddNewAssignment = () => {
    if (!newSelectedUser) return;
    if (newAssignments.some(a => a.user_id === newSelectedUser)) return;
    setNewAssignments([
      ...newAssignments,
      {
        user_id: newSelectedUser,
        assignment_weight: newAssignmentWeight,
        is_active: true,
        user: newUsers.find(u => u.id === newSelectedUser),
      },
    ]);
    setNewSelectedUser('');
    setNewAssignmentWeight(50);
  };

  const handleUpdateNewAssignmentWeight = (user_id: string, weight: number) => {
    setNewAssignments(assignments =>
      assignments.map(a => a.user_id === user_id ? { ...a, assignment_weight: weight } : a)
    );
  };

  const handleToggleNewAssignmentActive = (user_id: string) => {
    setNewAssignments(assignments =>
      assignments.map(a => a.user_id === user_id ? { ...a, is_active: !a.is_active } : a)
    );
  };

  const handleRemoveNewAssignment = (user_id: string) => {
    setNewAssignments(assignments => assignments.filter(a => a.user_id !== user_id));
  };

  // Stage management functions removed - now handled by individual PipelineStagesManager components

  // Fetch card layout config when opening the editor
  useEffect(() => {
    const fetchCardFields = async () => {
      if (!editingCardLayoutPipeline) return;
      setCardFieldsLoading(true);
      try {
        const { data, error } = await supabase
          .from('pipeline_card_info')
          .select('display_fields')
          .eq('pipeline_id', editingCardLayoutPipeline.id)
          .maybeSingle();
        if (error || !data) {
          setCardFields(DEFAULT_CARD_FIELDS);
        } else {
          setCardFields(data.display_fields || DEFAULT_CARD_FIELDS);
        }
      } catch (e) {
        setCardFields(DEFAULT_CARD_FIELDS);
      } finally {
        setCardFieldsLoading(false);
      }
    };
    const fetchPreviewProject = async () => {
      if (!editingCardLayoutPipeline) return;
      try {
        const { data, error } = await supabase
          .from('projects')
          .select('*')
          .eq('pipeline_id', editingCardLayoutPipeline.id)
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();
        if (!error && data) {
          setPreviewProject(data);
        } else {
          setPreviewProject(null);
        }
      } catch (e) {
        setPreviewProject(null);
      }
    };
    if (isCardLayoutEditorOpen) {
      fetchCardFields();
      fetchPreviewProject();
    }
  }, [isCardLayoutEditorOpen, editingCardLayoutPipeline]);

  const handleSaveCardFields = async () => {
    if (!editingCardLayoutPipeline) return;
    setCardFieldsSaving(true);
    try {
      // First check if a record exists
      const { data: existingData } = await supabase
        .from('pipeline_card_info')
        .select('id')
        .eq('pipeline_id', editingCardLayoutPipeline.id)
        .maybeSingle();

      if (existingData) {
        // Update existing record
        const { error } = await supabase
          .from('pipeline_card_info')
          .update({ display_fields: cardFields })
          .eq('pipeline_id', editingCardLayoutPipeline.id);
        
        if (error) throw error;
      } else {
        // Insert new record
        const tenantId = await getCurrentTenantId();
        
        const { error } = await supabase
          .from('pipeline_card_info')
          .insert({
            pipeline_id: editingCardLayoutPipeline.id,
            display_fields: cardFields,
            tenant_id: tenantId
          });
        
        if (error) throw error;
      }

      setIsCardLayoutEditorOpen(false);
      toast({ title: 'Success', description: 'Card layout updated.' });
    } catch (e) {
      console.error('Error saving card layout:', e);
      toast({ title: 'Error', description: 'Failed to save card layout.', variant: 'destructive' });
    } finally {
      setCardFieldsSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between w-full mb-4">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Pipeline Management</h2>
          <p className="text-sm text-muted-foreground">
            Configure pipeline stages and workflow settings
          </p>
        </div>
      </div>
          <Tabs value={activeType} onValueChange={v => setActiveType(v as 'commercial' | 'residential' | 'onboarding')} className="w-full mb-4">
            <div className="flex items-center justify-between mb-4">
              <TabsList className="grid w-max grid-cols-3 bg-muted/50 border border-border h-10 p-0.5">
                <TabsTrigger value="commercial" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm !h-8 !px-3 !py-0 !my-0 !mx-0.5 !rounded-sm flex items-center justify-center">Commercial Pipeline</TabsTrigger>
                <TabsTrigger value="residential" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm !h-8 !px-3 !py-0 !my-0 !mx-0.5 !rounded-sm flex items-center justify-center">Residential Pipeline</TabsTrigger>
                <TabsTrigger value="onboarding" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm !h-8 !px-3 !py-0 !my-0 !mx-0.5 !rounded-sm flex items-center justify-center">Internal Pipeline</TabsTrigger>
              </TabsList>
              <Button onClick={() => handleOpenDialog()}>
                + New Pipeline
              </Button>
            </div>
            <TabsContent value="commercial">
              <h3 className="text-base font-semibold mb-3 text-foreground">Pipelines</h3>
              <div className="overflow-hidden border rounded-lg shadow-sm">
                <div className="overflow-x-auto">
                  <Table className="min-w-full">
                    <TableHeader className="bg-muted">
                      <TableRow>
                        <TableHead className="font-semibold text-muted-foreground">Name</TableHead>
                        <TableHead className="font-semibold text-muted-foreground">Type</TableHead>
                        <TableHead className="font-semibold text-muted-foreground">Description</TableHead>
                        <TableHead className="font-semibold text-muted-foreground">Projects</TableHead>
                        <TableHead className="w-32 font-semibold text-muted-foreground">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody className="bg-card">
                      {isLoading ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground">Loading...</TableCell>
                        </TableRow>
                      ) : filteredPipelines.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground">No pipelines found</TableCell>
                        </TableRow>
                      ) : (
                        filteredPipelines.map((pipeline) => (
                          <TableRow key={pipeline.id} className="hover:bg-muted">
                            <TableCell className="font-medium text-foreground">
                              {pipeline.name}
                            </TableCell>
                            <TableCell>
                              <span className="inline-block px-2 py-1 text-xs rounded-full bg-muted text-muted-foreground">
                                {pipeline.pipeline_type}
                              </span>
                            </TableCell>
                            <TableCell className="text-foreground">{pipeline.description}</TableCell>
                            <TableCell className="text-foreground">{typeof pipeline.project_count === 'number' ? pipeline.project_count : 0}</TableCell>
                            <TableCell>
                              <div className="flex justify-end space-x-1">                            
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleOpenChecklistDialog(pipeline)}
                                      className="text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-200"
                                    >
                                      <ClipboardList className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Manage Checklists</p>
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleOpenAccessDialog(pipeline)}
                                      className="text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-200"
                                    >
                                      <Shield className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Manage Access</p>
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleOpenDialog(pipeline)}
                                      className="text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-200"
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Edit Pipeline</p>
                                  </TooltipContent>
                                </Tooltip>
                                <PipelineStagesManager
                                  pipelineId={pipeline.id}
                                  pipelineName={pipeline.name}
                                  icon="sun"
                                  onStagesUpdated={() => {
                                    // Refresh data if needed
                                    console.log('Stages updated for pipeline:', pipeline.id);
                                  }}
                                />
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => {
                                        setEditingCardLayoutPipeline(pipeline);
                                        setIsCardLayoutEditorOpen(true);
                                      }}
                                      className="text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-200"
                                    >
                                      <LayoutTemplate className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Edit Card Layout</p>
                                  </TooltipContent>
                                </Tooltip>
                                <DropdownMenu>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <DropdownMenuTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-200 relative"
                                        >
                                          <FileText className="w-4 h-4" />
                                          {hasTemplateUpdate(pipeline) && (
                                            <span className="absolute top-0 right-0 flex h-2 w-2">
                                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                                              <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
                                            </span>
                                          )}
                                        </Button>
                                      </DropdownMenuTrigger>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Template</p>
                                    </TooltipContent>
                                  </Tooltip>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuLabel>Template Actions</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleOpenSaveTemplateDialog(pipeline)}>
                                      <Upload className="mr-2 h-4 w-4" />
                                      Save as Template
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleOpenLoadTemplateDialog(pipeline)}>
                                      <Download className="mr-2 h-4 w-4" />
                                      Load Template
                                    </DropdownMenuItem>
                                    {pipeline.template_id && hasTemplateUpdate(pipeline) && (
                                      <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={() => handleOpenSyncConfirmation(pipeline)}>
                                          <RefreshCw className="mr-2 h-4 w-4" />
                                          Sync with Template
                                        </DropdownMenuItem>
                                      </>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleDeletePipeline(pipeline)}
                                      disabled={typeof pipeline.project_count === 'number' && pipeline.project_count > 0}
                                      className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Delete Pipeline</p>
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="residential">
              <h3 className="text-base font-semibold mb-1 text-foreground">Pipelines</h3>
              <div className="overflow-hidden border rounded-lg shadow-sm">
                <div className="overflow-x-auto">
                  <Table className="min-w-full">
                    <TableHeader className="bg-muted">
                      <TableRow>
                        <TableHead className="font-semibold text-muted-foreground">Name</TableHead>
                        <TableHead className="font-semibold text-muted-foreground">Type</TableHead>
                        <TableHead className="font-semibold text-muted-foreground">Description</TableHead>
                        <TableHead className="font-semibold text-muted-foreground">Projects</TableHead>
                        <TableHead className="w-32 font-semibold text-muted-foreground">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody className="bg-card">
                      {isLoading ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground">Loading...</TableCell>
                        </TableRow>
                      ) : filteredPipelines.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground">No pipelines found</TableCell>
                        </TableRow>
                      ) : (
                        filteredPipelines.map((pipeline) => (
                          <TableRow key={pipeline.id} className="hover:bg-muted">
                            <TableCell className="font-medium text-foreground">
                              {pipeline.name}
                            </TableCell>
                            <TableCell>
                              <span className="inline-block px-2 py-1 text-xs rounded-full bg-muted text-muted-foreground">
                                {pipeline.pipeline_type}
                              </span>
                            </TableCell>
                            <TableCell className="text-foreground">{pipeline.description}</TableCell>
                            <TableCell className="text-foreground">{typeof pipeline.project_count === 'number' ? pipeline.project_count : 0}</TableCell>
                            <TableCell>
                              <div className="flex justify-end space-x-1">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleOpenChecklistDialog(pipeline)}
                                      className="text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-200"
                                    >
                                      <ClipboardList className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Manage Checklists</p>
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleOpenAccessDialog(pipeline)}
                                      className="text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-200"
                                    >
                                      <Shield className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Manage Access</p>
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleOpenDialog(pipeline)}
                                      className="text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-200"
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Edit Pipeline</p>
                                  </TooltipContent>
                                </Tooltip>
                                <PipelineStagesManager
                                  pipelineId={pipeline.id}
                                  pipelineName={pipeline.name}
                                  icon="sun"
                                  onStagesUpdated={() => {
                                    // Refresh data if needed
                                    console.log('Stages updated for pipeline:', pipeline.id);
                                  }}
                                />
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => {
                                        setEditingCardLayoutPipeline(pipeline);
                                        setIsCardLayoutEditorOpen(true);
                                      }}
                                      className="text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-200"
                                    >
                                      <LayoutTemplate className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Edit Card Layout</p>
                                  </TooltipContent>
                                </Tooltip>
                                <DropdownMenu>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <DropdownMenuTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-200 relative"
                                        >
                                          <FileText className="w-4 h-4" />
                                          {hasTemplateUpdate(pipeline) && (
                                            <span className="absolute top-0 right-0 flex h-2 w-2">
                                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                                              <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
                                            </span>
                                          )}
                                        </Button>
                                      </DropdownMenuTrigger>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Template</p>
                                    </TooltipContent>
                                  </Tooltip>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuLabel>Template Actions</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleOpenSaveTemplateDialog(pipeline)}>
                                      <Upload className="mr-2 h-4 w-4" />
                                      Save as Template
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleOpenLoadTemplateDialog(pipeline)}>
                                      <Download className="mr-2 h-4 w-4" />
                                      Load Template
                                    </DropdownMenuItem>
                                    {pipeline.template_id && hasTemplateUpdate(pipeline) && (
                                      <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={() => handleOpenSyncConfirmation(pipeline)}>
                                          <RefreshCw className="mr-2 h-4 w-4" />
                                          Sync with Template
                                        </DropdownMenuItem>
                                      </>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleDeletePipeline(pipeline)}
                                      disabled={typeof pipeline.project_count === 'number' && pipeline.project_count > 0}
                                      className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Delete Pipeline</p>
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="onboarding">
              <h3 className="text-base font-semibold mb-1 text-foreground">Pipelines</h3>
              <div className="overflow-hidden border rounded-lg shadow-sm">
                <div className="overflow-x-auto">
                  <Table className="min-w-full">
                    <TableHeader className="bg-muted">
                      <TableRow>
                        <TableHead className="font-semibold text-muted-foreground">Name</TableHead>
                        <TableHead className="font-semibold text-muted-foreground">Type</TableHead>
                        <TableHead className="font-semibold text-muted-foreground">Description</TableHead>
                        <TableHead className="font-semibold text-muted-foreground">Projects</TableHead>
                        <TableHead className="w-32 font-semibold text-muted-foreground">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody className="bg-card">
                      {isLoading ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground">Loading...</TableCell>
                        </TableRow>
                      ) : filteredPipelines.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground">No pipelines found</TableCell>
                        </TableRow>
                      ) : (
                        filteredPipelines.map((pipeline) => (
                          <TableRow key={pipeline.id} className="hover:bg-muted">
                            <TableCell className="font-medium text-foreground">
                              {pipeline.name}
                            </TableCell>
                            <TableCell>
                              <span className="inline-block px-2 py-1 text-xs rounded-full bg-muted text-muted-foreground">
                                {pipeline.pipeline_type}
                              </span>
                            </TableCell>
                            <TableCell className="text-foreground">{pipeline.description}</TableCell>
                            <TableCell className="text-foreground">{typeof pipeline.project_count === 'number' ? pipeline.project_count : 0}</TableCell>
                            <TableCell>
                              <div className="flex justify-end space-x-1">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleOpenChecklistDialog(pipeline)}
                                      className="text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-200"
                                    >
                                      <ClipboardList className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Manage Checklists</p>
                                  </TooltipContent>
                                </Tooltip>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleOpenDialog(pipeline)}
                                  className="text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-200"
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <PipelineStagesManager
                                  pipelineId={pipeline.id}
                                  pipelineName={pipeline.name}
                                  icon="sun"
                                  onStagesUpdated={() => {
                                    // Refresh data if needed
                                    console.log('Stages updated for pipeline:', pipeline.id);
                                  }}
                                />
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => {
                                        setEditingCardLayoutPipeline(pipeline);
                                        setIsCardLayoutEditorOpen(true);
                                      }}
                                      className="text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-200"
                                    >
                                      <LayoutTemplate className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Edit Card Layout</p>
                                  </TooltipContent>
                                </Tooltip>
                                <DropdownMenu>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <DropdownMenuTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-200 relative"
                                        >
                                          <FileText className="w-4 h-4" />
                                          {hasTemplateUpdate(pipeline) && (
                                            <span className="absolute top-0 right-0 flex h-2 w-2">
                                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                                              <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
                                            </span>
                                          )}
                                        </Button>
                                      </DropdownMenuTrigger>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Template</p>
                                    </TooltipContent>
                                  </Tooltip>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuLabel>Template Actions</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleOpenSaveTemplateDialog(pipeline)}>
                                      <Upload className="mr-2 h-4 w-4" />
                                      Save as Template
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleOpenLoadTemplateDialog(pipeline)}>
                                      <Download className="mr-2 h-4 w-4" />
                                      Load Template
                                    </DropdownMenuItem>
                                    {pipeline.template_id && hasTemplateUpdate(pipeline) && (
                                      <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={() => handleOpenSyncConfirmation(pipeline)}>
                                          <RefreshCw className="mr-2 h-4 w-4" />
                                          Sync with Template
                                        </DropdownMenuItem>
                                      </>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleDeletePipeline(pipeline)}
                                      disabled={typeof pipeline.project_count === 'number' && pipeline.project_count > 0}
                                      className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Delete Pipeline</p>
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <SlidePanel
            isOpen={isDialogOpen}
            onClose={() => setIsDialogOpen(false)}
            title={editingPipeline ? 'Edit Pipeline' : 'Create New Pipeline'}
            width="xl"
            topOffset="4.5rem"
            showEdgeCloseButton={true}
            footer={
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={isCreatingPipeline}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  className=""
                  disabled={isCreatingPipeline}
                >
                  {isCreatingPipeline ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Creating...
                    </>
                  ) : (
                    editingPipeline ? 'Update Pipeline' : 'Create Pipeline'
                  )}
                </Button>
              </div>
            }
          >
            <div className="text-sm text-muted-foreground mb-6">
              {editingPipeline ? 'Update the pipeline details, assignments, and settings.' : 'Create a new pipeline with custom stages and settings.'}
            </div>

            {!editingPipeline && (
              <div className="flex space-x-2 mb-6">
                <Button
                  variant={creationMode === 'new' ? 'default' : 'outline'}
                  onClick={handleCreateFromScratch}
                  className={creationMode === 'new' ? '' : ''}
                >
                  Create New
                </Button>
                <Button
                  variant={creationMode === 'template' ? 'default' : 'outline'}
                  onClick={() => setCreationMode('template')}
                  className={creationMode === 'template' ? '' : ''}
                >
                  Load from Template
                </Button>
              </div>
            )}
              <div className="space-y-4 py-4">
                {!editingPipeline && creationMode === 'template' && (
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium">Select Template to Load</Label>
                      <Select
                        value={selectedTemplate?.id || ''}
                        onValueChange={async (value) => {
                          const template = pipelineTemplates.find(t => t.id === value);
                          if (template) {
                            await handleSelectTemplate(template);
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose a template to load" />
                        </SelectTrigger>
                        <SelectContent>
                          {pipelineTemplates
                            .filter(template => template.pipeline_type === activeType)
                            .map((template) => (
                              <SelectItem key={template.id} value={template.id}>
                                {template.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {selectedTemplate && (
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="load-checklists"
                            checked={loadChecklistTemplates}
                            onCheckedChange={setLoadChecklistTemplates}
                          />
                          <Label htmlFor="load-checklists" className="text-sm font-medium cursor-pointer">
                            Also load checklist templates for stages
                          </Label>
                        </div>
                        
                        {loadChecklistTemplates && (
                          <div className="ml-6 space-y-2">
                            {availableChecklistTemplates.length > 0 ? (
                              <div className="text-sm text-muted-foreground">
                                <div className="flex items-center justify-between">
                                  <p className="font-medium text-green-600 dark:text-green-400">
                                    Found {availableChecklistTemplates.length} matching checklist template{availableChecklistTemplates.length !== 1 ? 's' : ''}
                                  </p>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowChecklistDetails(true)}
                                    className="text-xs"
                                  >
                                    View Details
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="text-sm text-muted-foreground">
                                <p className="text-orange-600 dark:text-orange-400">
                                  No matching checklist templates found for the stages in this pipeline template.
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter pipeline name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type">Type</Label>
                  <Select
                    value={formData.pipeline_type}
                    onValueChange={(value: 'commercial' | 'residential' | 'onboarding') => 
                      setFormData({ ...formData, pipeline_type: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select pipeline type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="commercial">Commercial</SelectItem>
                      <SelectItem value="residential">Residential</SelectItem>
                      <SelectItem value="onboarding">Onboarding</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Description"
                    rows={3}
                  />
                </div>
                {editingPipeline && (
                  <PipelineAssignments pipelineId={editingPipeline.id} pipelineName={editingPipeline.name} />
                )}
                {!editingPipeline && (
                  <>
                    <hr className="my-4" />
                    <div className="mt-2">
                      <span className="font-semibold text-foreground">Project Assignments (optional)</span>
                      <div className="flex items-center justify-between mb-2 mt-2">
                        <div />
                        <div className="flex items-center space-x-2">
                          <Label htmlFor="even-split">Even Split</Label>
                          <Switch
                            id="even-split"
                            checked={newIsEvenSplit}
                            onCheckedChange={setNewIsEvenSplit}
                          />
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 items-end mb-4">
                        <div className="flex-1 min-w-[180px]">
                          <Label className="mb-1 block">Add User</Label>
                          <SearchableSelect
                            id="user"
                            value={newSelectedUser}
                            onValueChange={setNewSelectedUser}
                            options={newUsers.map(user => ({
                              label: `${user.first_name} ${user.last_name}`,
                              value: user.id
                            }))}
                            placeholder="Select a user"
                          />
                        </div>
                        {!newIsEvenSplit && (
                          <div className="w-24">
                            <Label className="mb-1 block">Weight %</Label>
                            <Input
                              id="weight-input"
                              type="number"
                              min="1"
                              max="100"
                              value={newAssignmentWeight}
                              onChange={e => setNewAssignmentWeight(parseInt(e.target.value) || 1)}
                            />
                          </div>
                        )}
                        <Button onClick={handleAddNewAssignment} className="h-10 font-semibold px-6 bg-primary hover:bg-primary/90 text-primary-foreground">
                          Add
                        </Button>
                      </div>
                      {newAssignments.length === 0 ? (
                        <div className="py-4 text-center text-muted-foreground">No assignments added</div>
                      ) : (
                        <div className="flex flex-col gap-2">
                          {newAssignments.map(a => (
                            <div key={a.user_id} className="flex items-center justify-between p-3 border rounded-lg bg-muted/50">
                              <div className="flex-1">
                                <p className="font-medium">{a.user?.first_name} {a.user?.last_name}</p>
                                <p className="text-sm text-muted-foreground">{a.user?.email}</p>
                              </div>
                              <div className="flex items-center gap-4">
                                {!newIsEvenSplit && (
                                  <div className="w-20">
                                    <Input
                                      type="number"
                                      min="1"
                                      max="100"
                                      value={a.assignment_weight}
                                      onChange={e => handleUpdateNewAssignmentWeight(a.user_id, parseInt(e.target.value) || 1)}
                                      className="h-8"
                                    />
                                  </div>
                                )}
                                <div className="flex items-center gap-2">
                                  <Switch
                                    checked={a.is_active}
                                    onCheckedChange={() => handleToggleNewAssignmentActive(a.user_id)}
                                  />
                                  <span className="text-sm text-muted-foreground">
                                    {a.is_active ? 'Active' : 'Inactive'}
                                  </span>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveNewAssignment(a.user_id)}
                                  className="text-destructive hover:text-destructive"
                                  aria-label="Remove"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Permissions Section */}
                    <hr className="my-4" />
                    <div className="mt-2">
                      <div className="flex items-center space-x-2 mb-4">
                        <Switch
                          id="permissions-enabled"
                          checked={newPipelinePermissionsEnabled}
                          onCheckedChange={setNewPipelinePermissionsEnabled}
                        />
                        <Label htmlFor="permissions-enabled" className="font-semibold cursor-pointer">
                          Enable Role Permissions
                        </Label>
                      </div>
                      
                      {newPipelinePermissionsEnabled && (
                        <div className="space-y-4">
                          {newPipelineAccessLoading ? (
                            <div className="flex items-center justify-center py-4">
                              <div className="flex items-center space-x-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                                <span className="text-sm text-muted-foreground">Loading access configuration...</span>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div>
                                <label className="text-sm font-medium mb-2 block">
                                  Pipeline Access Control
                                </label>
                                <PipelineAccessSelect
                                  roleOptions={roleOptions}
                                  userOptions={userOptions}
                                  teamOptions={teamOptions}
                                  selectedRoles={newPipelineSelectedRoles}
                                  selectedUsers={newPipelineSelectedUsers}
                                  selectedTeams={newPipelineSelectedTeams}
                                  onRolesChange={setNewPipelineSelectedRoles}
                                  onUsersChange={setNewPipelineSelectedUsers}
                                  onTeamsChange={setNewPipelineSelectedTeams}
                                  placeholder="Select teams, roles and users that can access this pipeline..."
                                  disabled={false}
                                  maxDisplay={3}
                                />
                              </div>
                              
                              {/* Information panel */}
                              <div className="bg-accent/5 border border-accent/20 rounded-md p-3">
                                <div className="flex items-start gap-2">
                                  <div className="bg-accent/50 rounded-full p-1 flex-shrink-0">
                                    <svg className="w-3 h-3 text-background" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                  <div>
                                    <h4 className="text-sm font-medium text-primary">Access Control Information</h4>
                                    <div className="text-xs text-muted-foreground mt-1 space-y-1">
                                      <p>• <strong>No permissions:</strong> Pipeline visible to all users</p>
                                      <p>• <strong>Role-based:</strong> All users with selected roles can access</p>
                                      <p>• <strong>User-based:</strong> Only specific selected users can access</p>
                                      <p>• <strong>Team-based:</strong> All users in selected teams can access</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
          </SlidePanel>

          {/* Checklist Templates Details Dialog */}
          <SlidePanel
            isOpen={showChecklistDetails}
            onClose={() => setShowChecklistDetails(false)}
            title="Checklist Templates to be Loaded"
            width="lg"
            topOffset="4.5rem"
            showEdgeCloseButton={true}
            footer={
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setShowChecklistDetails(false)}>
                  Close
                </Button>
              </div>
            }
          >
            <div className="text-sm text-muted-foreground mb-6">
              These checklist templates will be created for the matching stages in your new pipeline.
            </div>
              <div className="overflow-y-auto max-h-[60vh] space-y-3">
                {availableChecklistTemplates.map((template, index) => (
                  <div key={index} className="border rounded-lg p-3 bg-muted/30">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-sm">{template.name}</h4>
                      {template.stage_name && (
                        <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full">
                          {template.stage_name}
                        </span>
                      )}
                    </div>
                    {template.description && (
                      <p className="text-xs text-muted-foreground mb-2">{template.description}</p>
                    )}
                    {template.configuration?.items?.length > 0 && (
                      <div className="text-xs text-muted-foreground">
                        <span className="font-medium">{template.configuration.items.length} checklist item{template.configuration.items.length !== 1 ? 's' : ''}</span>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {template.configuration.items.slice(0, 3).map((item: any, itemIndex: number) => (
                            <span key={itemIndex} className="inline-block px-1.5 py-0.5 bg-accent/20 rounded text-xs">
                              {item.name}
                            </span>
                          ))}
                          {template.configuration.items.length > 3 && (
                            <span className="text-xs text-muted-foreground">
                              +{template.configuration.items.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
          </SlidePanel>

          {/* Custom stage dialogs removed - now using PipelineStagesManager components */}

          {editingCardLayoutPipeline && (
            <SlidePanel
              isOpen={isCardLayoutEditorOpen}
              onClose={() => setIsCardLayoutEditorOpen(false)}
              title="Edit Card Layout"
              width="2xl"
              topOffset="4.5rem"
              showEdgeCloseButton={true}
              footer={
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setIsCardLayoutEditorOpen(false)} disabled={cardFieldsSaving}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveCardFields} disabled={cardFieldsSaving || cardFieldsLoading}>
                    {cardFieldsSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Save Changes
                  </Button>
                </div>
              }
            >
              <div className="text-sm text-muted-foreground mb-6">
                Add, remove, reorder, or relabel the fields shown on cards for this pipeline. Project name, address, and avatar are always shown.
              </div>
                <div className="flex flex-col md:flex-row flex-wrap gap-8 items-start">
                  <div className="flex-1 min-w-[320px]">
                    {cardFieldsLoading ? (
                      <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
                    ) : (
                      <CardLayoutEditor 
                        value={cardFields} 
                        onChange={setCardFields} 
                        project={previewProject} 
                      />
                    )}
                  </div>
                  <div className="max-w-[360px] w-full border rounded-lg p-4 bg-muted/50">
                    <div className="font-semibold mb-2 text-center">Live Preview</div>
                    {cardFieldsLoading ? (
                      <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
                    ) : (
                      <CardLayoutEditorPreview fields={cardFields} project={previewProject} />
                    )}
                  </div>
                </div>
            </SlidePanel>
          )}

          {/* Pipeline Checklists Dialog */}
          <SlidePanel
            isOpen={isChecklistDialogOpen}
            onClose={closeChecklistDialog}
            title={`Manage Checklists - ${selectedPipelineForChecklists?.name || ''}`}
            width="xl"
            topOffset="4.5rem"
            showEdgeCloseButton={true}
            footer={
              <div className="flex justify-end">
                <Button variant="outline" onClick={closeChecklistDialog}>
                  Close
                </Button>
              </div>
            }
          >
            <div className="text-sm text-muted-foreground mb-6">
              Create and manage checklist templates for this pipeline. These checklists will be available for projects using this pipeline.
            </div>
              <div className="py-4 overflow-y-auto flex-1">
                {selectedPipelineForChecklists && (
                  <ChecklistsTab 
                    registerExitGuard={(g) => { checklistExitGuard.current = g; }}
                    pipelineId={selectedPipelineForChecklists.id}
                    pipelineType={selectedPipelineForChecklists.pipeline_type}
                    onRefreshData={() => {
                      // Refresh data if needed
                    }} 
                  />
                )}
              </div>
          </SlidePanel>

          {/* Pipeline Access Management Dialog */}
          <SlidePanel
            isOpen={isAccessDialogOpen}
            onClose={() => setIsAccessDialogOpen(false)}
            title={
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                Manage Access - {selectedPipelineForAccess?.name}
              </div>
            }
            width="lg"
            topOffset="4.5rem"
            showEdgeCloseButton={true}
            footer={
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setIsAccessDialogOpen(false)}
                  disabled={accessSaving}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveAccess}
                  disabled={accessSaving || accessLoading}
                  className=""
                >
                  {accessSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    'Save Access Settings'
                  )}
                </Button>
              </div>
            }
          >
            <div className="text-sm text-muted-foreground mb-6">
              Control which teams, roles and users can access this pipeline. If no permissions are set, the pipeline will be visible to all users.
            </div>
              <div className="py-4">
                {accessLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                      <span className="text-sm text-muted-foreground">Loading access configuration...</span>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        Pipeline Access Control
                      </label>
                      <PipelineAccessSelect
                        roleOptions={roleOptions}
                        userOptions={userOptions}
                        teamOptions={teamOptions}
                        selectedRoles={selectedRoles}
                        selectedUsers={selectedUsers}
                        selectedTeams={selectedTeams}
                        onRolesChange={setSelectedRoles}
                        onUsersChange={setSelectedUsers}
                        onTeamsChange={setSelectedTeams}
                        placeholder="Select teams, roles and users that can access this pipeline..."
                        disabled={accessSaving}
                        maxDisplay={3}
                      />
                    </div>
                    
                    {/* Information panel */}
                    <div className="bg-accent/5 border border-accent/20 rounded-md p-3">
                      <div className="flex items-start gap-2">
                        <div className="bg-accent/50 rounded-full p-1 flex-shrink-0">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-primary">Access Control Information</h4>
                          <div className="text-xs text-muted-foreground mt-1 space-y-1">
                            <p>• <strong>No permissions set:</strong> Pipeline visible to all users</p>
                            <p>• <strong>Role-based:</strong> All users with selected roles can access</p>
                            <p>• <strong>User-based:</strong> Only specific selected users can access</p>
                            <p>• <strong>Combined:</strong> Users matching either role OR user criteria can access</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
          </SlidePanel>

          {/* Save as Template Dialog */}
          <SlidePanel
            isOpen={isSaveTemplateDialogOpen}
            onClose={() => setIsSaveTemplateDialogOpen(false)}
            title="Save Pipeline as Template"
            width="md"
            footer={
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setIsSaveTemplateDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveAsTemplateClick}
                  disabled={
                    isSavingTemplate ||
                    (templateSaveMode === 'new' && !newTemplateName.trim()) ||
                    (templateSaveMode === 'overwrite' && !selectedTemplateToOverwrite)
                  }
                >
                  {isSavingTemplate && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {templateSaveMode === 'overwrite' ? 'Overwrite Template' : 'Create Template'}
                </Button>
              </div>
            }
          >
            <div className="space-y-6">
              <div className="text-sm text-muted-foreground">
                Choose whether to create a new template or overwrite an existing one.
              </div>

              {/* Mode Selection */}
              <div className="space-y-3">
                <Label>Save Mode</Label>
                <div className="flex gap-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      value="new"
                      checked={templateSaveMode === 'new'}
                      onChange={(e) => setTemplateSaveMode(e.target.value as 'new' | 'overwrite')}
                      className="w-4 h-4"
                    />
                    <span>Create New Template</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      value="overwrite"
                      checked={templateSaveMode === 'overwrite'}
                      onChange={(e) => setTemplateSaveMode(e.target.value as 'new' | 'overwrite')}
                      className="w-4 h-4"
                      disabled={availableTemplates.length === 0}
                    />
                    <span>Overwrite Existing Template</span>
                  </label>
                </div>
              </div>

              {/* New Template Name */}
              {templateSaveMode === 'new' && (
                <div className="space-y-2">
                  <Label htmlFor="template-name">Template Name *</Label>
                  <Input
                    id="template-name"
                    value={newTemplateName}
                    onChange={(e) => setNewTemplateName(e.target.value)}
                    placeholder="Enter template name"
                  />
                </div>
              )}

              {/* Template Selection for Overwrite */}
              {templateSaveMode === 'overwrite' && (
                <div className="space-y-2">
                  <Label htmlFor="template-select">Select Template to Overwrite *</Label>
                  {availableTemplates.length === 0 ? (
                    <div className="text-sm text-muted-foreground p-4 bg-muted rounded-md">
                      No existing templates found for this pipeline type. Please create a new template instead.
                    </div>
                  ) : (
                    <Select
                      value={selectedTemplateToOverwrite || ''}
                      onValueChange={setSelectedTemplateToOverwrite}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a template to overwrite" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableTemplates.map((template) => (
                          <SelectItem key={template.id} value={template.id}>
                            <div className="flex items-center gap-2">
                              <div className="flex flex-col flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{template.name}</span>
                                  {template.scope === 'global' ? (
                                    <Badge variant="outline" className="text-xs">
                                      <Globe className="h-3 w-3 mr-1" />
                                      Global
                                    </Badge>
                                  ) : (
                                    <Badge variant="secondary" className="text-xs">
                                      <Users className="h-3 w-3 mr-1" />
                                      Local
                                    </Badge>
                                  )}
                                </div>
                                {template.description && (
                                  <span className="text-xs text-muted-foreground">{template.description}</span>
                                )}
                              </div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {/* Warning for overwrite */}
              {templateSaveMode === 'overwrite' && selectedTemplateToOverwrite && (
                <div className="bg-orange-50 border border-orange-200 rounded-md p-3">
                  <div className="flex items-start gap-2">
                    <div className="bg-orange-500 rounded-full p-1 flex-shrink-0">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-orange-800">Warning</h4>
                      <p className="text-xs text-orange-700 mt-1">
                        This will replace all stages and checklist templates in the selected template. This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </SlidePanel>

          {/* Delete Confirmation Dialog */}
          <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete Pipeline</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete this pipeline? This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              {pipelineToDelete && (
                <div className="py-4">
                  <p className="text-sm text-muted-foreground">
                    Pipeline: <span className="font-medium text-foreground">{pipelineToDelete.name}</span>
                  </p>
                </div>
              )}
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsDeleteDialogOpen(false);
                    setPipelineToDelete(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={confirmDeletePipeline}
                >
                  Delete Pipeline
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Overwrite Confirmation Dialog */}
          <Dialog open={showOverwriteConfirmation} onOpenChange={setShowOverwriteConfirmation}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Confirm Template Overwrite</DialogTitle>
                <DialogDescription>
                  Are you sure you want to overwrite the selected template?
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <div className="bg-orange-50 border border-orange-200 rounded-md p-4">
                  <div className="flex items-start gap-3">
                    <div className="bg-orange-500 rounded-full p-1.5 flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-orange-800 mb-1">This action cannot be undone</h4>
                      <ul className="text-xs text-orange-700 space-y-1">
                        <li>• All existing stages will be replaced</li>
                        <li>• All existing checklist templates will be deleted</li>
                        <li>• The template version will be incremented</li>
                        <li>• Previous version data cannot be recovered</li>
                      </ul>
                    </div>
                  </div>
                </div>
                {selectedTemplateToOverwrite && (
                  <p className="text-sm text-muted-foreground mt-4">
                    Template: <span className="font-medium">
                      {availableTemplates.find(t => t.id === selectedTemplateToOverwrite)?.name || 'Unknown'}
                    </span>
                  </p>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setShowOverwriteConfirmation(false)}
                  disabled={isSavingTemplate}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleSaveAsTemplate}
                  disabled={isSavingTemplate}
                >
                  {isSavingTemplate && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Yes, Overwrite Template
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Load Template Dialog */}
          <Dialog open={isLoadTemplateDialogOpen} onOpenChange={setIsLoadTemplateDialogOpen}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Load Template</DialogTitle>
                <DialogDescription>
                  Select a template to load into "{pipelineToLoadTemplate?.name}". This will replace all existing stages.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="bg-orange-50 border border-orange-200 rounded-md p-4">
                  <div className="flex items-start gap-3">
                    <div className="bg-orange-500 rounded-full p-1.5 flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-orange-800 mb-1">Warning</h4>
                      <ul className="text-xs text-orange-700 space-y-1">
                        <li>• All existing stages will be removed</li>
                        <li>• Projects in this pipeline will remain unchanged</li>
                        <li>• This action cannot be undone</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="template-select">Select Template</Label>
                  <Select
                    value={selectedTemplateToLoad || ''}
                    onValueChange={setSelectedTemplateToLoad}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a template" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTemplates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{template.name}</span>
                            {template.scope === 'global' ? (
                              <Badge variant="outline" className="text-xs">
                                <Globe className="h-3 w-3 mr-1" />
                                Global
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs">
                                <Users className="h-3 w-3 mr-1" />
                                Local
                              </Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedTemplateToLoad && (
                    <p className="text-xs text-muted-foreground">
                      {availableTemplates.find(t => t.id === selectedTemplateToLoad)?.description || ''}
                    </p>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsLoadTemplateDialogOpen(false);
                    setPipelineToLoadTemplate(null);
                    setSelectedTemplateToLoad(null);
                  }}
                  disabled={isLoadingTemplate}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleLoadTemplate}
                  disabled={!selectedTemplateToLoad || isLoadingTemplate}
                >
                  {isLoadingTemplate && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Load Template
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Sync with Template Confirmation Dialog */}
          <Dialog open={isSyncConfirmationOpen} onOpenChange={(open) => {
            if (!open) {
              // Dialog is closing - use aggressive cleanup
              flushSync(() => {
                setIsSyncConfirmationOpen(false);
                setPipelineToSync(null);
                setPipelineStages([]);
                setTemplateStages([]);
                setPipelineChecklistsData([]);
                setTemplateChecklistsData([]);
              });

              // Force cleanup of any stuck dialog overlays/body styles
              setTimeout(() => {
                document.body.style.pointerEvents = '';
                document.body.style.overflow = '';
                const dialogs = document.querySelectorAll('[data-radix-dialog-overlay]');
                dialogs.forEach(el => el.remove());
              }, 50);
            } else {
              setIsSyncConfirmationOpen(true);
            }
          }}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Sync Pipeline & Template</DialogTitle>
                <DialogDescription>
                  Review changes between "{pipelineToSync?.name}" and "{pipelineToSync?.template?.name}"
                </DialogDescription>
              </DialogHeader>

              {isLoadingComparison ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="space-y-4 py-4">
                  {/* Version Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <div className="flex items-start gap-3">
                      <div className="bg-blue-500 rounded-full p-1.5 flex-shrink-0 mt-0.5">
                        <RefreshCw className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-blue-800 mb-1">Version Information</h4>
                        <p className="text-xs text-blue-700">
                          Pipeline version: v{pipelineToSync?.template_version} → Template version: v{pipelineToSync?.template?.template_version}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Sync Direction Selection */}
                  <div className="space-y-2">
                    <Label>Sync Direction</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant={syncDirection === 'template-to-pipeline' ? 'default' : 'outline'}
                        className="text-xs h-auto py-3 flex flex-col items-center gap-1"
                        onClick={() => setSyncDirection('template-to-pipeline')}
                      >
                        <Download className="h-4 w-4" />
                        <span>Template → Pipeline</span>
                        <span className="text-[10px] opacity-70">Use template stages</span>
                      </Button>
                      <Button
                        variant={syncDirection === 'pipeline-to-template' ? 'default' : 'outline'}
                        className="text-xs h-auto py-3 flex flex-col items-center gap-1"
                        onClick={() => setSyncDirection('pipeline-to-template')}
                      >
                        <Upload className="h-4 w-4" />
                        <span>Pipeline → Template</span>
                        <span className="text-[10px] opacity-70">Update template</span>
                      </Button>
                    </div>
                  </div>

                  {/* Compact Changes View - Only Show Differences */}
                  <div className="space-y-4">
                    {(() => {
                      // Calculate changes
                      const stagesToAdd = templateStages.filter((ts: any) =>
                        !pipelineStages.some((ps: any) => ps.name.toLowerCase().trim() === ts.name.toLowerCase().trim())
                      );
                      const stagesToRemove = pipelineStages.filter((ps: any) =>
                        !templateStages.some((ts: any) => ts.name.toLowerCase().trim() === ps.name.toLowerCase().trim())
                      );

                      // Detect modified stages (same name but different properties)
                      const stagesToModify = templateStages.filter((ts: any) => {
                        const matchingPipelineStage = pipelineStages.find((ps: any) =>
                          ps.name.toLowerCase().trim() === ts.name.toLowerCase().trim()
                        );
                        if (!matchingPipelineStage) return false;

                        // Normalize values for comparison (treat null, undefined, and empty string as equivalent)
                        const normalizeValue = (val: any) => val || null;

                        // Check if any properties actually differ
                        const descriptionDifferent = normalizeValue(matchingPipelineStage.description) !== normalizeValue(ts.description);
                        const slaDifferent = Boolean(matchingPipelineStage.sla_enabled) !== Boolean(ts.sla_enabled);
                        const permissionsDifferent = JSON.stringify(matchingPipelineStage.permissions || null) !== JSON.stringify(ts.permissions || null);
                        const slaSettingsDifferent = JSON.stringify(matchingPipelineStage.sla_settings || null) !== JSON.stringify(ts.sla_settings || null);

                        return descriptionDifferent || slaDifferent || permissionsDifferent || slaSettingsDifferent;
                      }).map((ts: any) => {
                        const ps = pipelineStages.find((p: any) =>
                          p.name.toLowerCase().trim() === ts.name.toLowerCase().trim()
                        );
                        return { template: ts, pipeline: ps };
                      });

                      const checklistsToAdd = templateChecklistsData.filter((tc: any) =>
                        !pipelineChecklistsData.some((pc: any) => pc.name.toLowerCase().trim() === tc.name.toLowerCase().trim())
                      );
                      const checklistsToRemove = pipelineChecklistsData.filter((pc: any) =>
                        !templateChecklistsData.some((tc: any) => tc.name.toLowerCase().trim() === pc.name.toLowerCase().trim())
                      );

                      // Detect items added/removed within existing checklists
                      const checklistItemChanges: any[] = [];

                      templateChecklistsData.forEach((tc: any) => {
                        const matchingPipelineChecklist = pipelineChecklistsData.find((pc: any) =>
                          pc.name.toLowerCase().trim() === tc.name.toLowerCase().trim()
                        );
                        if (!matchingPipelineChecklist) return;

                        // Get all template items
                        const templateConfig = tc.configuration || {};
                        const allTemplateItems = [
                          ...(templateConfig.items || []),
                          ...(templateConfig.ungrouped_items || []),
                          ...(templateConfig.groups?.flatMap((g: any) => g.items || []) || [])
                        ];

                        // Get all pipeline items
                        const allPipelineItems = matchingPipelineChecklist.items || [];

                        // Find items that exist in template but not in pipeline (added items)
                        const addedItems = allTemplateItems.filter((ti: any) => {
                          const tiName = (ti.name || ti.item_name || ti.item_text || '').toLowerCase().trim();
                          return !allPipelineItems.some((pi: any) =>
                            (pi.item_name || pi.item_text || '').toLowerCase().trim() === tiName
                          );
                        });

                        // Find items that exist in pipeline but not in template (removed items)
                        const removedItems = allPipelineItems.filter((pi: any) => {
                          const piName = (pi.item_name || pi.item_text || '').toLowerCase().trim();
                          return !allTemplateItems.some((ti: any) =>
                            (ti.name || ti.item_name || ti.item_text || '').toLowerCase().trim() === piName
                          );
                        });

                        if (addedItems.length > 0 || removedItems.length > 0) {
                          checklistItemChanges.push({
                            checklistName: tc.name,
                            addedItems,
                            removedItems
                          });
                        }
                      });

                      const hasChanges = syncDirection === 'template-to-pipeline' &&
                        (stagesToAdd.length > 0 || stagesToRemove.length > 0 || stagesToModify.length > 0 ||
                         checklistsToAdd.length > 0 || checklistsToRemove.length > 0 || checklistItemChanges.length > 0);

                      if (syncDirection === 'pipeline-to-template') {
                        return (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                              <Upload className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                              <div className="flex-1">
                                <h4 className="font-semibold text-sm text-blue-900 mb-2">Updating Template</h4>
                                <div className="text-xs text-blue-700 space-y-1">
                                  <div>• All current pipeline configuration will be saved to template</div>
                                  <div>• Template version: v{pipelineToSync?.template?.template_version} → v{(pipelineToSync?.template?.template_version || 0) + 1}</div>
                                  <div>• Other pipelines using this template will be notified of the update</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      }

                      if (!hasChanges) {
                        return (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-center gap-3">
                              <Circle className="h-5 w-5 text-green-600 flex-shrink-0" />
                              <div>
                                <h4 className="font-semibold text-sm text-green-900">No Changes Detected</h4>
                                <p className="text-xs text-green-700 mt-1">Pipeline is already in sync with template</p>
                              </div>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div className="space-y-3">
                          {/* Stages to Add */}
                          {stagesToAdd.length > 0 && (
                            <div className="border border-green-200 bg-green-50 rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <Plus className="h-4 w-4 text-green-600" />
                                <h4 className="font-semibold text-sm text-green-900">
                                  {stagesToAdd.length} Stage{stagesToAdd.length !== 1 ? 's' : ''} to Add
                                </h4>
                              </div>
                              <div className="space-y-1.5 ml-6">
                                {stagesToAdd.map((stage: any, idx: number) => (
                                  <div key={idx} className="text-xs">
                                    <div className="font-medium text-green-900">{stage.name}</div>
                                    {stage.description && (
                                      <div className="text-green-700 text-[11px]">{stage.description}</div>
                                    )}
                                    <div className="text-green-600 text-[10px] flex gap-2 mt-0.5">
                                      {stage.sla_enabled && <span>• SLA Enabled</span>}
                                      {stage.permissions && <span>• Role Permissions</span>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Stages to Remove */}
                          {stagesToRemove.length > 0 && (
                            <div className="border border-red-200 bg-red-50 rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <Trash2 className="h-4 w-4 text-red-600" />
                                <h4 className="font-semibold text-sm text-red-900">
                                  {stagesToRemove.length} Stage{stagesToRemove.length !== 1 ? 's' : ''} to Remove
                                </h4>
                              </div>
                              <div className="space-y-1.5 ml-6">
                                {stagesToRemove.map((stage: any) => (
                                  <div key={stage.id} className="text-xs">
                                    <div className="font-medium text-red-900">{stage.name}</div>
                                    {stage.description && (
                                      <div className="text-red-700 text-[11px]">{stage.description}</div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Stages to Modify */}
                          {stagesToModify.length > 0 && (
                            <div className="border border-yellow-200 bg-yellow-50 rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <Pencil className="h-4 w-4 text-yellow-600" />
                                <h4 className="font-semibold text-sm text-yellow-900">
                                  {stagesToModify.length} Stage{stagesToModify.length !== 1 ? 's' : ''} to Update
                                </h4>
                              </div>
                              <div className="space-y-2 ml-6">
                                {stagesToModify.map((change: any, idx: number) => (
                                  <div key={idx} className="text-xs">
                                    <div className="font-medium text-yellow-900 mb-1">{change.template.name}</div>
                                    <div className="space-y-0.5 text-[11px]">
                                      {change.pipeline.description !== change.template.description && (
                                        <div className="text-yellow-700">
                                          <span className="text-yellow-600">Description:</span>{' '}
                                          <span className="line-through">{change.pipeline.description || 'None'}</span>{' '}
                                          → {change.template.description || 'None'}
                                        </div>
                                      )}
                                      {change.pipeline.sla_enabled !== change.template.sla_enabled && (
                                        <div className="text-yellow-700">
                                          <span className="text-yellow-600">SLA:</span>{' '}
                                          {change.pipeline.sla_enabled ? 'Enabled' : 'Disabled'} → {change.template.sla_enabled ? 'Enabled' : 'Disabled'}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Checklists to Add */}
                          {checklistsToAdd.length > 0 && (
                            <div className="border border-green-200 bg-green-50 rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <Plus className="h-4 w-4 text-green-600" />
                                <h4 className="font-semibold text-sm text-green-900">
                                  {checklistsToAdd.length} Checklist{checklistsToAdd.length !== 1 ? 's' : ''} to Add
                                </h4>
                              </div>
                              <div className="space-y-2.5 ml-6">
                                {checklistsToAdd.map((checklist: any) => {
                                  const config = checklist.configuration || {};
                                  const allItems = [
                                    ...(config.items || []),
                                    ...(config.ungrouped_items || []),
                                    ...(config.groups?.flatMap((g: any) => g.items || []) || [])
                                  ];

                                  return (
                                    <div key={checklist.id} className="text-xs">
                                      <div className="font-medium text-green-900 mb-1">
                                        {checklist.name}
                                        {checklist.stage_name && (
                                          <span className="text-green-600 font-normal ml-1.5">
                                            (Stage: {checklist.stage_name})
                                          </span>
                                        )}
                                      </div>
                                      {allItems.length > 0 && (
                                        <div className="ml-3 space-y-0.5 text-[11px] text-green-700">
                                          {allItems.slice(0, 5).map((item: any, idx: number) => (
                                            <div key={idx} className="flex items-center gap-1.5">
                                              <span className="text-green-500">▪</span>
                                              <span>{item.name || item.item_name || item.item_text}</span>
                                              {item.item_type && (
                                                <span className="text-green-600 text-[10px]">({item.item_type})</span>
                                              )}
                                            </div>
                                          ))}
                                          {allItems.length > 5 && (
                                            <div className="text-green-600 text-[10px] italic">
                                              +{allItems.length - 5} more item{allItems.length - 5 !== 1 ? 's' : ''}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      {config.groups && config.groups.length > 0 && (
                                        <div className="text-green-600 text-[10px] mt-1">
                                          {config.groups.length} group{config.groups.length !== 1 ? 's' : ''}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Checklists to Remove */}
                          {checklistsToRemove.length > 0 && (
                            <div className="border border-red-200 bg-red-50 rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <Trash2 className="h-4 w-4 text-red-600" />
                                <h4 className="font-semibold text-sm text-red-900">
                                  {checklistsToRemove.length} Checklist{checklistsToRemove.length !== 1 ? 's' : ''} to Remove
                                </h4>
                              </div>
                              <div className="space-y-2 ml-6">
                                {checklistsToRemove.map((checklist: any) => {
                                  const itemCount = checklist.items?.length || 0;
                                  const groupCount = checklist.groups?.length || 0;

                                  return (
                                    <div key={checklist.id} className="text-xs">
                                      <div className="font-medium text-red-900">
                                        {checklist.name}
                                        <span className="text-red-600 font-normal ml-1.5 text-[11px]">
                                          ({itemCount} item{itemCount !== 1 ? 's' : ''}
                                          {groupCount > 0 && `, ${groupCount} group${groupCount !== 1 ? 's' : ''}`})
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Checklist Items Added/Removed */}
                          {checklistItemChanges.map((change: any, changeIdx: number) => (
                            <div key={changeIdx}>
                              {/* Items Added */}
                              {change.addedItems.length > 0 && (
                                <div className="border border-green-200 bg-green-50 rounded-lg p-3">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Plus className="h-4 w-4 text-green-600" />
                                    <h4 className="font-semibold text-sm text-green-900">
                                      {change.addedItems.length} Item{change.addedItems.length !== 1 ? 's' : ''} Added to "{change.checklistName}"
                                    </h4>
                                  </div>
                                  <div className="ml-6 space-y-0.5 text-xs">
                                    {change.addedItems.map((item: any, idx: number) => (
                                      <div key={idx} className="flex items-center gap-1.5 text-green-700">
                                        <span className="text-green-500">▪</span>
                                        <span>{item.name || item.item_name || item.item_text}</span>
                                        {item.item_type && (
                                          <span className="text-green-600 text-[10px]">({item.item_type})</span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Items Removed */}
                              {change.removedItems.length > 0 && (
                                <div className="border border-red-200 bg-red-50 rounded-lg p-3">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Trash2 className="h-4 w-4 text-red-600" />
                                    <h4 className="font-semibold text-sm text-red-900">
                                      {change.removedItems.length} Item{change.removedItems.length !== 1 ? 's' : ''} Removed from "{change.checklistName}"
                                    </h4>
                                  </div>
                                  <div className="ml-6 space-y-0.5 text-xs">
                                    {change.removedItems.map((item: any, idx: number) => (
                                      <div key={idx} className="flex items-center gap-1.5 text-red-700">
                                        <span className="text-red-500">▪</span>
                                        <span>{item.item_name || item.item_text}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Warning based on sync direction */}
                  <div className="bg-orange-50 border border-orange-200 rounded-md p-4">
                      <div className="flex items-start gap-3">
                        <div className="bg-orange-500 rounded-full p-1.5 flex-shrink-0 mt-0.5">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-orange-800 mb-1">Warning</h4>
                          <ul className="text-xs text-orange-700 space-y-1">
                            {syncDirection === 'template-to-pipeline' && (
                              <>
                                <li>• Pipeline stages will be replaced with template stages</li>
                                <li>• Projects in pipeline will remain unchanged</li>
                                <li>• This action cannot be undone</li>
                              </>
                            )}
                            {syncDirection === 'pipeline-to-template' && (
                              <>
                                <li>• Template will be updated with pipeline stages</li>
                                <li>• Template version will increment</li>
                                <li>• All pipelines using this template will see an update notification</li>
                              </>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                </div>
              )}

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    // Use flushSync for immediate state updates
                    flushSync(() => {
                      setIsSyncConfirmationOpen(false);
                      setPipelineToSync(null);
                      setPipelineStages([]);
                      setTemplateStages([]);
                      setPipelineChecklistsData([]);
                      setTemplateChecklistsData([]);
                    });

                    // Force cleanup of any stuck dialog overlays/body styles
                    setTimeout(() => {
                      document.body.style.pointerEvents = '';
                      document.body.style.overflow = '';
                      const dialogs = document.querySelectorAll('[data-radix-dialog-overlay]');
                      dialogs.forEach(el => el.remove());
                    }, 50);
                  }}
                  disabled={isLoadingTemplate}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSyncWithTemplate}
                  disabled={isLoadingTemplate || isLoadingComparison}
                >
                  {isLoadingTemplate && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {syncDirection === 'template-to-pipeline' && 'Apply Template'}
                  {syncDirection === 'pipeline-to-template' && 'Update Template'}
                  {syncDirection === 'merge' && 'Mark as Synced'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
    </div>
  );
} 